import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
// REMOVED: getFinancialStats - now using financialData prop from API instead of direct Supabase queries
// REMOVED: useScoreData - now calculating scoreMetrics from financialData prop to avoid duplicate API calls
import { updateMember } from '../../api/memberApi';
import { getMemberHistory, type MemberHistoryPoint } from '../../services/lenderApiService';
// BUG-UI-100/104/106 FIX: Import audit evidence service to fetch factor scores from algo_run_evidence
import { getRunEvidence } from '../../services/auditEvidenceService';
// DEMO2-only: synthesize 24-month chart history from algo_run_evidence when /history is sparse.
import { buildFallbackHistoryFromEvidence, overlayLatestMonth } from '../../utils/demo2EvidenceFallback';
import type { Member } from '../../types/member';
import type { FinancialHealthStats } from '../../types/financialStats';
import { DebugLayout, DebugSection, useDebug } from '../debug/DebugLayout';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { ErrorMessage } from '../common/ErrorMessage';
import { calculateScoreMetricsFromStats } from '../../utils/scoreCalculations';
// Audit traceability: track when lender expands a member card for detailed view
import { recordMemberExpanded } from '../../utils/lenderSessionTracking';
import { format, parseISO } from 'date-fns';
// REMOVED: useDataSource - no longer needed since we use API data
import {
  AreaChart,
  Area,
  CartesianGrid,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import { VeraScoreChart } from '../charts/VeraScoreChart';
import { GrossIncomeChart } from '../charts/GrossIncomeChart';
import { MinimumDebtPaymentsChart } from '../charts/MinimumDebtPaymentsChart';
import { MonthlyExpensesChart } from '../charts/MonthlyExpensesChart';
import { MoneyInChart } from '../charts/MoneyInChart';
import { MoneyLeftoverChart } from '../charts/MoneyLeftoverChart';
import { NetSavingsChart } from '../charts/NetSavingsChart';
import { AdverseDebtChart } from '../charts/AdverseDebtChart';
import { PaymentHistoryChart } from '../charts/PaymentHistoryChart';
import { NumberOfLatePaymentsChart } from '../charts/NumberOfLatePaymentsChart';
import { DebtToIncomeChart } from '../charts/DebtToIncomeChart';
import { getStatusMetadata, DashboardStatus } from '../../types/memberStatus';
import { getHealthStatus } from '../../utils/healthStatus';
import checkIcon from '../../assets/icons/check-2.svg';
import crownIcon from '../../assets/icons/crown.svg';
import warningIcon from '../../assets/icons/warning.svg';
import scalesIcon from '../../assets/icons/scales-balance.svg';
import atRiskIcon from '../../assets/icons/atRisk.svg';
import DebtToIncomeFactorChart from '../charts/DebtToIncomeFactorChart';
import SpendingFactorChart from '../charts/SpendingFactorChart';
import DebtFactorChart from '../charts/DebtFactorChart';
import CashOnHandFactorChart from '../charts/CashOnHandFactorChart';
import { ActualCashOnHandChart } from '../charts/ActualCashOnHandChart';
import SavingsFactorChart from '../charts/SavingsFactorChart';
import PaymentHistoryFactorChart from '../charts/PaymentHistoryFactorChart';
import { AIChatHistory } from '../chat/AIChatHistory';
import ConnectCardsDndKitInline from './ConnectCardsDndKitInline';
import DraggableConnectSections from './DraggableConnectSections';
import ComplianceLogSection from './ComplianceLogSection';
import { AuditReportPanel } from './AuditReportPanel';
import { useVeraScoreComparison } from '../../hooks/useVeraScoreComparison';
// FIXED: import path for constants
import { FACTORS, FINANCIAL_METRICS } from '../../constants';
import { CreditScoreChartWrapper } from '../charts/CreditScoreChartWrapper';
import runnerIcon from '../../assets/icons/runner.svg';
// FIXED: import path to match context directory
import { useMobileChart } from '../../context/MobileChartContext';
import ScoreTrigger from './ScoreTrigger';
import FactorTrigger from './FactorTrigger';
import MetricTrigger from './MetricTrigger';
import ScoreComparisonCard from './ScoreComparisonCard';
import { ClickableMetric } from '../audit/ClickableMetric';
import { useEvidenceModal, DashboardValueData } from '../../context/EvidenceContext';
import CreditJourneyTimeline, { type TimelineEvent } from './CreditJourneyTimeline';
import './CreditJourneyTimeline.css';

// ============================================================================
// PHASE 5 FEATURE FLAG - Set to false to revert to original header layout
// ============================================================================
const ENABLE_PHASE5_HEADER_ENHANCEMENTS = true;

// Phase 5 Helper Functions
// Calculate trend from current score vs 6-month average
interface TrendResult {
  direction: 'up' | 'down' | 'stable';
  changePercent: number;
}

const calculateTrend = (currentScore: number, sixMonthScore?: number): TrendResult => {
  if (!sixMonthScore || sixMonthScore === 0) {
    return { direction: 'stable', changePercent: 0 };
  }
  const change = ((currentScore - sixMonthScore) / sixMonthScore) * 100;
  return {
    direction: change > 2 ? 'up' : change < -2 ? 'down' : 'stable',
    changePercent: Math.abs(change),
  };
};

// Get star rating display based on verascore_rating
const getRatingStars = (rating?: string): { count: number; color: string } => {
  const map: Record<string, { count: number; color: string }> = {
    'Critical': { count: 1, color: 'text-red-500' },
    'Fair': { count: 2, color: 'text-yellow-500' },
    'Healthy': { count: 3, color: 'text-blue-500' },
    'Excellent': { count: 5, color: 'text-green-500' },
  };
  return map[rating || ''] || { count: 0, color: 'text-gray-400' };
};

// Build timeline events from member data and history
const buildTimelineEvents = (
  member: Member,
  history: MemberHistoryPoint[] | null
): TimelineEvent[] => {
  const events: TimelineEvent[] = [];

  // Event 1: Consent Granted (if available)
  if (member.inst_member_since) {
    events.push({
      date: member.inst_member_since,
      title: 'Consent Granted',
      description: 'Member authorized data sharing with lender',
      type: 'consent',
    });
  }

  // Events 2-5: Historical VeraScore milestones
  if (history && history.length > 0) {
    const sortedHistory = [...history].sort((a, b) => a.month.localeCompare(b.month));
    const latestScore = sortedHistory[sortedHistory.length - 1];
    const latestDate = new Date(`${latestScore.month}-01`);

    // Helper to find score closest to target months ago
    const findScoreAtMonthsAgo = (monthsAgo: number) => {
      const targetDate = new Date(latestDate);
      targetDate.setMonth(targetDate.getMonth() - monthsAgo);
      const targetMonth = targetDate.toISOString().slice(0, 7); // YYYY-MM format

      // Find exact match or closest earlier month
      return sortedHistory.find(h => h.month === targetMonth) ||
             sortedHistory.reverse().find(h => h.month <= targetMonth);
    };

    // 24 months ago (2 years)
    const score24mo = findScoreAtMonthsAgo(24);
    if (score24mo) {
      events.push({
        date: `${score24mo.month}-01`,
        title: '2 Years Ago',
        description: `VeraScore: ${score24mo.score} (${score24mo.rating})`,
        type: 'milestone',
      });
    }

    // 12 months ago (1 year)
    const score12mo = findScoreAtMonthsAgo(12);
    if (score12mo && score12mo.month !== score24mo?.month) {
      events.push({
        date: `${score12mo.month}-01`,
        title: '12 Months Ago',
        description: `VeraScore: ${score12mo.score} (${score12mo.rating})`,
        type: 'milestone',
      });
    }

    // 6 months ago
    const score6mo = findScoreAtMonthsAgo(6);
    if (score6mo && score6mo.month !== score12mo?.month && score6mo.month !== score24mo?.month) {
      events.push({
        date: `${score6mo.month}-01`,
        title: '6 Months Ago',
        description: `VeraScore: ${score6mo.score} (${score6mo.rating})`,
        type: 'milestone',
      });
    }

    // Latest score (current)
    events.push({
      date: `${latestScore.month}-01`,
      title: 'Latest VeraScore',
      description: `Current score: ${latestScore.score} (${latestScore.rating})`,
      type: 'latest_score',
    });
  }

  return events;
};

// US 7.36: Render trend arrow indicator (large version for main score)
const renderTrendArrow = (currentScore: number, sixMonthScore?: number): JSX.Element | null => {
  const trend = calculateTrend(currentScore, sixMonthScore);
  const pointsChange = sixMonthScore ? Math.round(currentScore - sixMonthScore) : 0;

  if (trend.direction === 'up') {
    return (
      <span
        className="inline-flex items-center text-green-500 ml-2"
        title={`Up ${pointsChange} points from 6-month average`}
      >
        <svg className="w-8 h-8 sm:w-10 sm:h-10" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
        </svg>
      </span>
    );
  } else if (trend.direction === 'down') {
    return (
      <span
        className="inline-flex items-center text-red-500 ml-2"
        title={`Down ${Math.abs(pointsChange)} points from 6-month average`}
      >
        <svg className="w-8 h-8 sm:w-10 sm:h-10" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M14.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 12.586V5a1 1 0 012 0v7.586l2.293-2.293a1 1 0 011.414 0z" clipRule="evenodd" />
        </svg>
      </span>
    );
  } else {
    return (
      <span
        className="inline-flex items-center text-gray-400 ml-2"
        title="No significant change from 6-month average"
      >
        <svg className="w-8 h-8 sm:w-10 sm:h-10" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </span>
    );
  }
};

// Demo2 fast-fix: small delta label (e.g. "+12" / "-5") under a historical score tile.
// Returns null when either value is missing so the tile collapses cleanly to em-dash.
const renderHistoricalDelta = (
  currentScore: number | null | undefined,
  historicalScore: number | null | undefined,
): JSX.Element | null => {
  if (!currentScore || !historicalScore) return null;
  const delta = Math.round(currentScore - historicalScore);
  if (delta === 0) {
    return <div className="text-white/60 text-[10px] sm:text-xs mt-0.5">±0</div>;
  }
  const sign = delta > 0 ? '+' : '';
  const color = delta > 0 ? 'text-green-300' : 'text-red-300';
  return (
    <div className={`${color} text-[10px] sm:text-xs mt-0.5`}>
      {sign}{delta}
    </div>
  );
};

// US 7.36: Render small trend arrow for factor cards
const renderSmallTrendArrow = (currentScore: number, sixMonthScore?: number): JSX.Element | null => {
  const trend = calculateTrend(currentScore, sixMonthScore);
  const pointsChange = sixMonthScore ? Math.round(currentScore - sixMonthScore) : 0;

  if (trend.direction === 'up') {
    return (
      <span
        className="inline-flex items-center text-green-500 ml-1"
        title={`Up ${pointsChange} points from 6-month average`}
      >
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
        </svg>
      </span>
    );
  } else if (trend.direction === 'down') {
    return (
      <span
        className="inline-flex items-center text-red-500 ml-1"
        title={`Down ${Math.abs(pointsChange)} points from 6-month average`}
      >
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M14.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 12.586V5a1 1 0 012 0v7.586l2.293-2.293a1 1 0 011.414 0z" clipRule="evenodd" />
        </svg>
      </span>
    );
  } else {
    return (
      <span
        className="inline-flex items-center text-gray-400 ml-1"
        title="No significant change from 6-month average"
      >
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </span>
    );
  }
};

// ============================================================================
// VS-BUG-002: Income Basis Badge Helper
// ============================================================================
import type { IncomeBasis, IncomeBasisMetadata } from '../../types/financialStats';

/**
 * BUG-UI-062: Render income basis badge from summary API or detailed stats
 * Supports both full IncomeBasisMetadata and simplified basis string from summary
 */
interface SimplifiedIncomeBasis {
  income_basis_used?: IncomeBasis | null;
  income_confidence?: number | null;
}

const renderIncomeBasisBadge = (
  incomeBasis?: IncomeBasisMetadata | SimplifiedIncomeBasis | null
): JSX.Element | null => {
  // Handle null/undefined - show Self-Reported as default
  if (!incomeBasis || !incomeBasis.income_basis_used) {
    return (
      <span
        className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800"
        title="Income source: User-reported (legacy data without basis tracking)"
      >
        Self-Reported
      </span>
    );
  }

  const basisConfig: Record<IncomeBasis, { label: string; bgColor: string; textColor: string; icon: string }> = {
    'VERIFIED_GROSS': {
      label: 'Verified',
      bgColor: 'bg-green-100',
      textColor: 'text-green-800',
      icon: '✓'
    },
    'DEPOSITED_NET': {
      label: 'Bank Verified',
      bgColor: 'bg-blue-100',
      textColor: 'text-blue-800',
      icon: '🏦'
    },
    'ATTESTED': {
      label: 'Self-Reported',
      bgColor: 'bg-amber-100',
      textColor: 'text-amber-800',
      icon: '📝'
    }
  };

  const config = basisConfig[incomeBasis.income_basis_used];
  if (!config) {
    // Unknown basis type - fallback
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
        Unknown
      </span>
    );
  }

  // Handle both full metadata and simplified version
  const confidencePercent = incomeBasis.income_confidence != null
    ? Math.round(incomeBasis.income_confidence * 100)
    : null;

  // Check for quality flags (only in full metadata)
  const fullMetadata = incomeBasis as IncomeBasisMetadata;
  const hasWarnings = fullMetadata.income_quality_flags?.some(
    f => f.severity === 'WARNING' || f.severity === 'ERROR'
  ) ?? false;

  // Build tooltip
  const tooltipParts: string[] = [];
  if (fullMetadata.selection_reason) {
    tooltipParts.push(fullMetadata.selection_reason);
  }
  if (confidencePercent != null) {
    tooltipParts.push(`Confidence: ${confidencePercent}%`);
  }
  if (fullMetadata.income_quality_flags?.length) {
    tooltipParts.push('Flags: ' + fullMetadata.income_quality_flags.map(f => f.description).join(', '));
  }

  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config.bgColor} ${config.textColor} ${hasWarnings ? 'ring-1 ring-amber-400' : ''}`}
      title={tooltipParts.join('\n') || `Income source: ${config.label}`}
    >
      <span className="mr-1">{config.icon}</span>
      {config.label}
      {confidencePercent != null && <span className="ml-1 opacity-75">({confidencePercent}%)</span>}
      {hasWarnings && <span className="ml-1">⚠️</span>}
    </span>
  );
};
// ============================================================================

// Define the FinancialMetric interface inline to avoid the import issue
interface FinancialMetric {
  key: keyof FinancialHealthStats | 'credit_score' | 'verascore';
  label: string;
  format: 'percentage' | 'currency' | 'number';
  description: string;
  chartType?: 'area' | 'bar' | 'composed';
  referenceLines?: {
    y: number;
    label: string;
    color: string;
  }[];
}

// Define FACTORS array type for the copied FactorDetails section
type Factor = {
  key: keyof FinancialHealthStats;
  label: string;
  description: string;
};

interface MemberCardProps {
  member: Member;
  /** Financial data passed from parent but currently unused. Kept for potential future use. */
  financialData: FinancialHealthStats[];
  /** Global month selected at page level (Option 3: Hybrid Date Selection) */
  globalMonth?: string | null;
  onAssist: () => void;
  onEmail: () => void;
}

const formatValue = (value: unknown, format: 'percentage' | 'currency' | 'number'): string => {
  const numValue = Number(value) || 0;
  switch (format) {
    case 'currency':
      return `$${numValue.toLocaleString()}`;
    case 'percentage':
      return `${numValue.toFixed(1)}%`;
    default:
      return numValue.toString();
  }
};

const formatAxisValue = (value: number, format: 'percentage' | 'currency' | 'number'): string => {
  switch (format) {
    case 'currency':
      return `$${(value / 1000).toFixed(0)}k`;
    case 'percentage':
      return `${value.toFixed(1)}%`;
    default:
      return value.toString();
  }
};

// Helper function to safely format currency values, handling null/undefined cases
// Added to prevent crashes when member.yearly_gross_income is undefined (rebuild fix)
const formatCurrency = (
  value: number | null | undefined,
  min: number = 2,
  max: number = 2
) => `$${Number(value ?? 0).toLocaleString(undefined, {
  minimumFractionDigits: min,
  maximumFractionDigits: max,
})}`;

// Helper function to get color based on VeraScore
const getVeraScoreColor = (score: number): string => {
  if (score >= 60) return '#22C55E'; // Green
  if (score >= 40) return '#3B82F6'; // Blue
  if (score >= 20) return '#F97316'; // Orange
  return '#EF4444'; // Red
};

const formatPhoneDisplay = (digits: string): string => {
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return digits;
};

// US States for select dropdown
const US_STATES = [
  { value: 'AL', label: 'Alabama' }, { value: 'AK', label: 'Alaska' }, { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' }, { value: 'CA', label: 'California' }, { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' }, { value: 'DE', label: 'Delaware' }, { value: 'DC', label: 'District of Columbia' },
  { value: 'FL', label: 'Florida' }, { value: 'GA', label: 'Georgia' }, { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' }, { value: 'IL', label: 'Illinois' }, { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' }, { value: 'KS', label: 'Kansas' }, { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' }, { value: 'ME', label: 'Maine' }, { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' }, { value: 'MI', label: 'Michigan' }, { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' }, { value: 'MO', label: 'Missouri' }, { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' }, { value: 'NV', label: 'Nevada' }, { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' }, { value: 'NM', label: 'New Mexico' }, { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' }, { value: 'ND', label: 'North Dakota' }, { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' }, { value: 'OR', label: 'Oregon' }, { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' }, { value: 'SC', label: 'South Carolina' }, { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' }, { value: 'TX', label: 'Texas' }, { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' }, { value: 'VA', label: 'Virginia' }, { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' }, { value: 'WI', label: 'Wisconsin' }, { value: 'WY', label: 'Wyoming' }
];

// Format a stored zipcode for display. Demo2 DB rows pack ZIP+4 as a 9-digit
// string with no delimiter (e.g. "207690438"); render as "20769-0438". 5-digit
// values pass through unchanged. Anything else is rendered as-is.
const formatZipcode = (zip: string | null | undefined): string => {
  if (!zip) return '';
  const digits = zip.replace(/\D/g, '');
  if (digits.length === 9) return `${digits.slice(0, 5)}-${digits.slice(5)}`;
  if (digits.length === 5) return digits;
  return zip;
};

const generateMockPhoneNumber = (uuid: string): { display: string; dial: string } => {
  const safeUuid = uuid || 'default-uuid';
  const hash = safeUuid.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const areaCode = 200 + (hash % 800);
  const exchange = 200 + ((hash * 7) % 800);
  const lineNumber = 1000 + ((hash * 13) % 9000);
  const dial = `${areaCode}${exchange}${String(lineNumber).padStart(4, '0')}`;
  const display = formatPhoneDisplay(dial);
  return { display, dial };
};

const getPhoneNumber = (
  phoneNumber: string | null | undefined,
  _uuid: string,
): { display: string; dial: string; synthesized: boolean } => {
  if (phoneNumber) {
    const dial = phoneNumber.replace(/\D/g, '');
    const display = formatPhoneDisplay(dial);
    return { display, dial, synthesized: false };
  }
  // Demo2 fast-fix: do not fabricate phone numbers when DB phone is null.
  // generateMockPhoneNumber() is retained for non-Demo2 callers that may import it.
  void generateMockPhoneNumber;
  return { display: 'No phone on file', dial: '', synthesized: true };
};

const renderChart = (
  metric: FinancialMetric,
  data: FinancialHealthStats[],
  showDebug: boolean,
  monthsToShow: number = 12,
  onExpandClick?: React.MutableRefObject<any>,
  /** BUG-UI-071A: Member context for evidence modal */
  /** BUG-UI-131: Added memberLatePayments for authoritative late payment count */
  memberContext?: { runId?: string; memberId?: string; memberName?: string; memberLatePayments?: number | null }
): JSX.Element | null => {
  // Sort data by date (oldest to newest) to match the factor charts
  const sortedData = [...data].sort((a, b) => {
    return new Date(a.month).getTime() - new Date(b.month).getTime();
  });

  // Remove duplicate months - keep only the most recent entry for each month
  const uniqueMonthsData: FinancialHealthStats[] = [];
  const monthsSet = new Set<string>();

  // Process the data in reverse (newest to oldest) to keep most recent entries
  [...sortedData].reverse().forEach(item => {
    if (!monthsSet.has(item.month)) {
      monthsSet.add(item.month);
      uniqueMonthsData.unshift(item); // Add to beginning to maintain chronological order
    }
  });

  const deduplicatedData = uniqueMonthsData;

  if (metric.key === 'verascore') {
    return (
      <VeraScoreChart
        data={deduplicatedData}
        showDebug={showDebug}
        monthsToShow={monthsToShow}
        onExpandClick={onExpandClick}
        runId={memberContext?.runId}
        memberId={memberContext?.memberId}
        memberName={memberContext?.memberName}
      />
    );
  }

  if (metric.key === 'credit_score') {
    return (
      <CreditScoreChartWrapper
        data={deduplicatedData}
        showDebug={showDebug}
        monthsToShow={monthsToShow}
        onExpandClick={onExpandClick}
        runId={memberContext?.runId}
        memberId={memberContext?.memberId}
        memberName={memberContext?.memberName}
      />
    );
  }

  if (metric.key === 'gross_income') {
    return (
      <GrossIncomeChart data={deduplicatedData} showDebug={showDebug} monthsToShow={monthsToShow} onExpandClick={onExpandClick} />
    );
  }

  if (metric.key === 'minimum_debt_payments') {
    return <MinimumDebtPaymentsChart data={deduplicatedData} showDebug={showDebug} monthsToShow={monthsToShow} onExpandClick={onExpandClick} />;
  }

  if (metric.key === 'monthly_expenses') {
    return <MonthlyExpensesChart data={deduplicatedData} showDebug={showDebug} monthsToShow={monthsToShow} onExpandClick={onExpandClick} />;
  }

  if (metric.key === 'money_in') {
    return <MoneyInChart data={deduplicatedData} showDebug={showDebug} monthsToShow={monthsToShow} onExpandClick={onExpandClick} />;
  }

  if (metric.key === 'money_leftover') {
    return <MoneyLeftoverChart data={deduplicatedData} showDebug={showDebug} monthsToShow={monthsToShow} onExpandClick={onExpandClick} />;
  }

  if (metric.key === 'net_savings') {
    return <NetSavingsChart data={deduplicatedData} showDebug={showDebug} monthsToShow={monthsToShow} onExpandClick={onExpandClick} />;
  }

  if (metric.key === 'total_outstanding_adverse_debt') {
    return <AdverseDebtChart data={deduplicatedData} showDebug={showDebug} monthsToShow={monthsToShow} onExpandClick={onExpandClick} />;
  }

  if (metric.key === 'age_most_recent_late') {
    // FIXED: align prop names with other charts
    return <PaymentHistoryChart data={deduplicatedData} showDebug={showDebug} />;
  }

  if (metric.key === 'number_of_late_payments') {
    // BUG-UI-131 FIX: Pass authoritative memberLatePayments from member data
    return <NumberOfLatePaymentsChart data={deduplicatedData} showDebug={showDebug} monthsToShow={monthsToShow} onExpandClick={onExpandClick} memberLatePayments={memberContext?.memberLatePayments} />;
  }

  if (metric.key === 'dti_ratio') {
    return <DebtToIncomeChart data={deduplicatedData} showDebug={showDebug} monthsToShow={monthsToShow} onExpandClick={onExpandClick} runId={memberContext?.runId} memberId={memberContext?.memberId} memberName={memberContext?.memberName} />;
  }

  if (metric.key === 'liquid_reserves') {
    return <ActualCashOnHandChart data={deduplicatedData} showDebug={showDebug} monthsToShow={monthsToShow} onExpandClick={onExpandClick} />;
  }

  // Default area chart for other metrics
  if (typeof metric.key === 'string' && !metric.key.includes('dti') && (metric.key as string) !== 'verascore' && (metric.key as string) !== 'credit_score') {
    return (
      <AreaChart data={deduplicatedData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
        <XAxis
          dataKey="month"
          tickFormatter={(date: string) => {
            try {
              return format(parseISO(date), 'MMM');
            } catch {
              return '';
            }
          }}
          tickMargin={10}
          style={{ fontSize: '0.5em' }}
          angle={315}
          textAnchor="end"
        />
        <YAxis
          tickFormatter={(value: number) => formatAxisValue(value, metric.format)}
          domain={(metric.key as string) === 'verascore' ? [0, 100] : ['auto', 'auto']}
        />
        <Tooltip
          formatter={(value: number) => formatValue(value, metric.format)}
          labelFormatter={(label: string) => {
            try {
              return format(parseISO(label), 'MMMM yyyy');
            } catch {
              return label;
            }
          }}
          contentStyle={{
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            borderRadius: '8px',
            border: '1px solid #E2E8F0',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          }}
        />
        <Area
          type="monotone"
          dataKey={metric.key as any}
          stroke="#1C4583"
          fill="#1C4583"
          fillOpacity={0.2}
          strokeWidth={2}
          dot={false}
          activeDot={{ r: 6, fill: '#1C4583' }}
        />
      </AreaChart>
    );
  }

  return null;
};

// Determine if member has unlocked credit opportunities (for badge)
// `overrideCreditScore` lets callers pass member.credit_score (Demo2 synthetic
// or real `credit_scores.score`) so this helper agrees with the rest of the UI.
const hasUnlockedCreditOpportunities = (
  stats: FinancialHealthStats[],
  overrideCreditScore?: number | null
): boolean => {
  if (!stats.length) return false;

  const currentStats = getCurrentMonthStats(stats);
  const veraScore = currentStats?.verascore || 0;
  const creditScore = getMockCreditScore(stats, overrideCreditScore);

  const veraScoreCategory = veraScore >= 70 ? 'excellent' :
    veraScore >= 50 ? 'good' :
      veraScore >= 35 ? 'fair' : 'poor';

  const creditScoreCategory = creditScore >= 720 ? 'excellent' :
    creditScore >= 680 ? 'good' :
      creditScore >= 630 ? 'fair' :
        creditScore >= 580 ? 'poor' : 'very poor';

  return (
    (veraScoreCategory === 'excellent' && creditScoreCategory !== 'excellent') ||
    (veraScoreCategory === 'good' && (creditScoreCategory === 'fair' || creditScoreCategory === 'poor' || creditScoreCategory === 'very poor')) ||
    (veraScore > 50 && (creditScore < 680)) ||
    (veraScore >= 60)
  );
};

// Mock credit score generation based on financial health
// `overrideCreditScore` short-circuits the synthetic calculation when the
// caller already has an authoritative value (member.credit_score from the
// API or the Demo2 persona overlay). UI must prefer the override.
const calculateMockCreditScore = (
  stats: FinancialHealthStats[],
  overrideCreditScore?: number | null
): number => {
  if (typeof overrideCreditScore === 'number' && overrideCreditScore > 0) {
    return overrideCreditScore;
  }
  if (!stats.length) return 620;

  const sortedStats = [...stats].sort((a, b) => new Date(a.month).getTime() - new Date(b.month).getTime());
  const latest = sortedStats[sortedStats.length - 1];
  if (!latest) return 620;

  const veraScore = latest.verascore ?? 0;
  let baseScore;

  if (veraScore >= 55) {
    const deterministicRandom = latest.uuid
      ? (parseInt(latest.uuid.replace(/[^0-9]/g, ''), 10) % 80)
      : 40;
    baseScore = 520 + deterministicRandom;
  } else {
    baseScore = 300 + Math.round((veraScore / 100) * 520);
  }

  const paymentImpact = Math.min(
    50,
    -20 * (latest.number_of_late_payments ?? 0) +
    Math.min(50, 5 * (latest.age_most_recent_late ?? 0))
  );

  const dtiRatio = latest.dti_ratio ?? 0;
  const dtiImpact = dtiRatio <= 36 ? 50 : dtiRatio <= 43 ? 25 : dtiRatio <= 50 ? 0 : -50;

  let score = Math.round(baseScore + paymentImpact + dtiImpact);
  score = Math.max(300, Math.min(820, score));

  const memberSpecificVariation = latest.uuid ? (parseInt(latest.uuid.replace(/[^0-9]/g, ''), 10) % 20) - 10 : 0;
  score += memberSpecificVariation;

  return score;
};

// Get the most recent credit score update - now with a cache to prevent recalculation
const creditScoreCache = new Map<string, number>();
const getMockCreditScore = (
  stats: FinancialHealthStats[],
  overrideCreditScore?: number | null
): number => {
  if (typeof overrideCreditScore === 'number' && overrideCreditScore > 0) {
    return overrideCreditScore;
  }
  if (!stats.length) return 620;
  const uuid = stats[0]?.uuid;
  if (!uuid) return 620;
  if (creditScoreCache.has(uuid)) {
    return creditScoreCache.get(uuid)!;
  }
  const sortedStats = [...stats].sort((a, b) => new Date(a.month).getTime() - new Date(b.month).getTime());
  const score = calculateMockCreditScore(sortedStats);
  creditScoreCache.set(uuid, score);
  return score;
};

// Option 3: Get credit score for a specific month's stats (no caching since month varies)
// BUG-DATA-001/002/003 FIX: Accept optional member data for late payments and DTI ratio
// Member data from lender_member_summary view is more accurate than stats from financial_health_stats_v2
const getCreditScoreForStat = (
  stat: FinancialHealthStats | undefined,
  memberOverrides?: {
    number_of_late_payments?: number | null;
    age_of_late_payments?: number | null;
    dti_ratio_value?: number | null;
    credit_score?: number | null;
  }
): number => {
  if (typeof memberOverrides?.credit_score === 'number' && memberOverrides.credit_score > 0) {
    return memberOverrides.credit_score;
  }
  if (!stat) return 620;

  const veraScore = stat.verascore ?? 0;
  let baseScore;

  if (veraScore >= 55) {
    const deterministicRandom = stat.uuid
      ? (parseInt(stat.uuid.replace(/[^0-9]/g, ''), 10) % 80)
      : 40;
    baseScore = 520 + deterministicRandom;
  } else {
    baseScore = 300 + Math.round((veraScore / 100) * 520);
  }

  // BUG-DATA-002 FIX: Prefer member.number_of_late_payments from lender_member_summary view
  const latePayments = memberOverrides?.number_of_late_payments ?? stat.number_of_late_payments ?? 0;
  const ageOfLate = memberOverrides?.age_of_late_payments ?? stat.age_most_recent_late ?? 0;
  const paymentImpact = Math.min(
    50,
    -20 * latePayments +
    Math.min(50, 5 * ageOfLate)
  );

  // BUG-DATA-001 FIX: Prefer member.dti_ratio_value from lender_member_summary view
  const dtiRatio = memberOverrides?.dti_ratio_value ?? stat.dti_ratio ?? 0;
  const dtiImpact = dtiRatio <= 36 ? 50 : dtiRatio <= 43 ? 25 : dtiRatio <= 50 ? 0 : -50;

  let score = Math.round(baseScore + paymentImpact + dtiImpact);
  score = Math.max(300, Math.min(820, score));

  const memberSpecificVariation = stat.uuid ? (parseInt(stat.uuid.replace(/[^0-9]/g, ''), 10) % 20) - 10 : 0;
  score += memberSpecificVariation;

  return score;
};

// Helper function to check if a member needs onboarding overlay
const needsOnboardingOverlay = (status: string): boolean => {
  return [
    DashboardStatus.INVITED,
    DashboardStatus.IN_PROGRESS,
    DashboardStatus.EMAIL_NOT_CONFIRMED,
    DashboardStatus.PENDING,
  ].includes(status as DashboardStatus);
};

// Helper function to get onboarding overlay message based on status
const getOnboardingMessage = (status: string): string => {
  switch (status) {
    case DashboardStatus.INVITED:
      return "Member invited but hasn't accepted. Follow up needed.";
    case DashboardStatus.IN_PROGRESS:
      return 'Onboarding started but incomplete. Guide member through next steps.';
    case DashboardStatus.EMAIL_NOT_CONFIRMED:
      return 'Email confirmation pending. Remind member to check inbox.';
    case DashboardStatus.PENDING:
      return 'Verification needed. Review documents and approve or request more info.';
    default:
      return 'Action required to complete onboarding. Check member status.';
  }
};

// Function to get larger status icon for the main score display
const getLargeStatusIcon = (score: number) => {
  if (score >= 80) return <img src={crownIcon} alt="Excellent" className="w-6 h-6" />;
  if (score >= 60) return <img src={checkIcon} alt="Healthy" className="w-6 h-6" />;
  if (score >= 40) return <img src={scalesIcon} alt="Fair" className="w-6 h-6" />;
  if (score >= 20) return <img src={warningIcon} alt="Needs Improvement" className="w-6 h-6" />;
  return <img src={atRiskIcon} alt="At Risk" className="w-6 h-6" />;
};

// Function to get status icon based on score
const getStatusIcon = (score: number) => {
  if (score >= 80) return <img src={crownIcon} alt="Excellent" className="w-6 h-6" />;
  if (score >= 60) return <img src={checkIcon} alt="Healthy" className="w-6 h-6" />;
  if (score >= 40) return <img src={scalesIcon} alt="Fair" className="w-6 h-6" />;
  if (score >= 20) return <img src={warningIcon} alt="Needs Improvement" className="w-6 h-6" />;
  return <img src={atRiskIcon} alt="At Risk" className="w-6 h-6" />;
};

// Function to render charts for factors
const renderFactorChart = (
  factor: Factor,
  data: FinancialHealthStats[],
  showDebug: boolean,
  monthsToShow: number = 12,
  ref?: React.MutableRefObject<any>,
  /** BUG-UI-071A: Member context for evidence modal */
  memberContext?: { runId?: string; memberId?: string; memberName?: string }
): JSX.Element | null => {
  const sortedData = [...data].sort((a, b) => {
    return new Date(a.month).getTime() - new Date(b.month).getTime();
  });

  const fullData = sortedData.slice(-24);

  if (factor.key === 'verascore') {
    return (
      <VeraScoreChart
        data={fullData}
        showDebug={showDebug}
        monthsToShow={monthsToShow}
        onExpandClick={ref}
        runId={memberContext?.runId}
        memberId={memberContext?.memberId}
        memberName={memberContext?.memberName}
      />
    );
  }

  if (factor.key === 'factor_dti') {
    return <DebtToIncomeFactorChart data={fullData} showDebug={showDebug} monthsToShow={monthsToShow} onExpandClick={ref} />;
  }

  if (factor.key === 'factor_spending') {
    return <SpendingFactorChart data={fullData} showDebug={showDebug} onExpandClick={ref} />;
  }

  if (factor.key === 'factor_debt') {
    return <DebtFactorChart data={fullData} showDebug={showDebug} onExpandClick={ref} />;
  }

  if (factor.key === 'factor_cash_on_hand') {
    return <CashOnHandFactorChart data={fullData} showDebug={showDebug} onExpandClick={ref} />;
  }

  if (factor.key === 'factor_savings') {
    return <SavingsFactorChart data={fullData} showDebug={showDebug} onExpandClick={ref} />;
  }

  if (factor.key === 'factor_payment_history') {
    return <PaymentHistoryFactorChart data={fullData} showDebug={showDebug} onExpandClick={ref} />;
  }

  // Fallback small area chart (rare path)
  const filteredData = sortedData.slice(-monthsToShow).map(stat => {
    const month = format(parseISO(stat.month), 'MMM yyyy');
    return {
      month,
      [factor.key]: stat[factor.key],
    } as any;
  });

  const isCurrency = ['factor_cash_on_hand', 'factor_debt'].includes(factor.key as unknown as string);

  return (
    <AreaChart data={filteredData}>
      <CartesianGrid strokeDasharray="3 3" />
      <XAxis
        dataKey="month"
        tickMargin={10}
        style={{ fontSize: '0.5em' }}
        angle={315}
        textAnchor="end"
      />
      <YAxis domain={[0, 100]} tickFormatter={(value: number) => formatAxisValue(value, isCurrency ? 'currency' : 'number')} />
      <Tooltip formatter={(value: number) => formatValue(value, isCurrency ? 'currency' : 'number')} />
      <Area
        type="monotone"
        dataKey={factor.key as any}
        stroke="#1C4583"
        fill="#1C4583"
        fillOpacity={0.2}
        strokeWidth={2}
        dot={false}
        activeDot={{ r: 6, fill: '#1C4583' }}
      />
    </AreaChart>
  );
};

// Function to generate factor analysis
const getFactorAnalysis = (
  factor: Factor,
  _stats: FinancialHealthStats[],
  currentScore: number,
  member: Member
): {
  trendAnalysis: {
    text: string;
    indicators: string[];
  };
  riskAssessment: {
    text: string;
    level: 'low' | 'medium' | 'high';
  };
  recommendations: {
    loanProducts: string[];
    advisoryPoints: string[];
  };
  veraScoreAdvisor?: {
    summary: string;
    inclusionOpportunities: string[];
    communityPrograms: string[];
    nextSteps: string[];
  };
} => {
  const defaultAnalysis = {
    trendAnalysis: {
      text: `Current ${factor.label} score is ${Math.round(currentScore)}.`,
      indicators: [],
    },
    riskAssessment: {
      text: 'Insufficient data for risk assessment.',
      level: 'medium' as const,
    },
    recommendations: {
      loanProducts: [],
      advisoryPoints: ['Continue monitoring financial health.'],
    },
  };

  if (factor.key === 'verascore') {
    const currentMonthStats = getCurrentMonthStats(_stats);
    const currentScoreNow = currentMonthStats?.verascore || 0;

    const sortedStats = [..._stats].sort((a, b) =>
      new Date(b.month).getTime() - new Date(a.month).getTime()
    );

    let sixMonthsAgo = currentScoreNow;
    let yearAgo = currentScoreNow;

    if (currentMonthStats) {
      const currentDate = new Date(currentMonthStats.month);
      const sixMonthsTarget = new Date(currentDate);
      sixMonthsTarget.setMonth(currentDate.getMonth() - 6);

      const yearAgoTarget = new Date(currentDate);
      yearAgoTarget.setMonth(currentDate.getMonth() - 12);

      for (const stat of sortedStats) {
        const statDate = new Date(stat.month);
        if (statDate >= sixMonthsTarget && statDate < currentDate) {
          sixMonthsAgo = stat.verascore || currentScoreNow;
        }
        if (statDate >= yearAgoTarget && statDate < sixMonthsTarget) {
          yearAgo = stat.verascore || currentScoreNow;
        }
      }
    }

    const trend = currentScoreNow - sixMonthsAgo;
    const yearTrend = currentScoreNow - yearAgo;

    const moneyLeftover = currentMonthStats?.money_leftover || 0;
    const creditScore = calculateMockCreditScore(_stats, member.credit_score);

    const veraScoreCategory = currentScoreNow >= 80 ? 'excellent' :
      currentScoreNow >= 60 ? 'good' :
        currentScoreNow >= 40 ? 'fair' : 'poor';

    const creditScoreCategory = creditScore >= 750 ? 'excellent' :
      creditScore >= 700 ? 'good' :
        creditScore >= 650 ? 'fair' :
          creditScore >= 600 ? 'poor' : 'very poor';

    const veraBetterThanCredit =
      (veraScoreCategory === 'excellent' && (creditScoreCategory === 'fair' || creditScoreCategory === 'poor' || creditScoreCategory === 'very poor')) ||
      (veraScoreCategory === 'good' && (creditScoreCategory === 'poor' || creditScoreCategory === 'very poor'));

    const creditBetterThanVera =
      (creditScoreCategory === 'excellent' && (veraScoreCategory === 'fair' || veraScoreCategory === 'poor')) ||
      (creditScoreCategory === 'good' && veraScoreCategory === 'poor');

    const analysis = {
      trendAnalysis: {
        text: `${member.first_name}'s VeraScore is ${currentScoreNow.toFixed(0)} (${
          trend !== 0 ? `${trend > 0 ? 'up' : 'down'} ${Math.abs(trend).toFixed(1)} points over 6 months, ` : ''
        }${
          yearTrend !== 0 ?
            `${yearTrend > 0 ? 'improved' : 'declined'} ${Math.abs(yearTrend).toFixed(1)} points over 12 months` :
            'stable over 12 months'
        }). ${
          currentScoreNow >= 80 ? `Strong loan candidate with excellent payment capacity. This score indicates minimal risk and qualifies for premium rates up to $${(moneyLeftover * 60).toLocaleString(undefined, { maximumFractionDigits: 0 })}.` :
          currentScoreNow >= 60 ? `Good loan potential with moderate payment capacity. Consider standard products with loan amounts up to $${(moneyLeftover * 48).toLocaleString(undefined, { maximumFractionDigits: 0 })}.` :
          currentScoreNow >= 40 ? `Exercise caution when determining loan eligibility. Recommend limited offerings up to $${(moneyLeftover * 24).toLocaleString(undefined, { maximumFractionDigits: 0 })} with protective terms.` :
          `Significant financial challenges present. Focus on improvement strategies before approving new credit products.`
        }`,
        indicators: [
          `Score Health: ${getHealthStatus(currentScoreNow)}`,
          `Score Range: 0-100`,
          `Credit Score: ${creditScore} (${creditScoreCategory.charAt(0).toUpperCase() + creditScoreCategory.slice(1)})`,
          `${veraBetterThanCredit ? '🔓 UNLOCKS OPPORTUNITY: VeraScore reveals untapped potential' :
            creditBetterThanVera ? '⚠️ RISK ALERT: VeraScore reveals hidden risk factors' :
              '✓ Consistent Assessment: Credit and VeraScore aligned'}`
        ],
      },
       riskAssessment: {
         text:
           currentScoreNow >= 70
             ? 'Low risk profile based on strong overall financial health.'
             : currentScoreNow >= 50
               ? 'Moderate risk profile with some areas for improvement.'
               : 'Higher risk profile requiring attention to multiple financial factors.',
         level: (currentScoreNow >= 70 ? 'low' : currentScoreNow >= 50 ? 'medium' : 'high') as 'low' | 'medium' | 'high',
       },
      recommendations: {
        loanProducts: [
          currentScoreNow >= 70
            ? 'Eligible for premium loan products with favorable terms'
            : currentScoreNow >= 50
              ? 'Standard loan products with moderate terms'
              : 'Limited loan options or higher interest products',
        ],
        advisoryPoints: [
          'Review individual factors for specific improvement areas',
          currentScoreNow < 70
            ? 'Focus on improving lowest-scoring factors first'
            : 'Maintain current financial habits',
          'Schedule regular financial health check-ins',
        ],
      },
      veraScoreAdvisor: {
        summary: `${member.first_name || 'This member'}'s VeraScore of ${Math.round(currentScoreNow)} indicates ${
          currentScoreNow >= 70 ? 'strong' : currentScoreNow >= 50 ? 'moderate' : 'developing'
        } financial health. ${
          currentScoreNow >= 70
            ? 'They are well-positioned for a variety of financial products.'
            : currentScoreNow >= 50
              ? 'They may qualify for standard products, but would benefit from improving specific areas.'
              : 'They require careful evaluation for lending and may benefit from financial counseling.'
        }`,
        inclusionOpportunities: [
          currentScoreNow >= 70
            ? 'Access to premium financial products and services'
            : currentScoreNow >= 50
              ? 'Eligible for standard financial products with reasonable terms'
              : 'Specialized financial products designed for credit building',
          veraBetterThanCredit ? 'VeraScore unlocks lending opportunities despite credit limitations' : '',
        ].filter(Boolean) as string[],
        communityPrograms: [
          'Financial literacy workshops',
          currentScoreNow < 50 ? 'Credit counseling services' : 'Wealth building seminars',
          'Community lending circles',
        ],
        nextSteps: [
          'Review detailed factor breakdown',
          'Schedule financial consultation',
          'Set specific improvement goals',
          veraBetterThanCredit ? 'Explore new lending options unlocked by VeraScore' :
            creditBetterThanVera ? 'Address financial health issues hidden by good credit history' : '',
        ].filter(Boolean) as string[],
      },
    };

    return analysis;
  }

  return defaultAnalysis;
};

// AdvisorNotesSection component
interface AdvisorNotesSectionProps {
  onAssist: () => void;
  onEmail: () => void;
  member: Member;
}

interface Note {
  id: string;
  text: string;
  author: string;
  avatar: string;
  timestamp: Date;
}

const AdvisorNotesSection = ({ onAssist, onEmail, member }: AdvisorNotesSectionProps) => {
  const advisorNoteRef = useRef<HTMLTextAreaElement>(null);
  const [advisorNotes, setAdvisorNotes] = useState<Note[]>([]);
  const [showNotesHistory, setShowNotesHistory] = useState<boolean>(false);

  // Function to load notes from localStorage
  const loadNotes = () => {
    if (member?.uuid) {
      const savedNotes = localStorage.getItem(`member_notes_${member.uuid}`);
      if (savedNotes) {
        try {
          const parsedNotes = JSON.parse(savedNotes);
          const notesWithDates = parsedNotes.map((note: Note) => ({
            ...note,
            timestamp: new Date(note.timestamp)
          }));
          setAdvisorNotes(notesWithDates);
        } catch (error) {
          console.error('Error loading notes:', error);
          setAdvisorNotes([]);
        }
      } else {
        setAdvisorNotes([]);
      }
    }
  };

  // Load notes when member changes
  useEffect(() => {
    loadNotes();
  }, [member?.uuid]);

  // Listen for notes updates from Assist Member modal
  useEffect(() => {
    const handleNotesUpdated = (event: CustomEvent) => {
      if (event.detail.memberUuid === member?.uuid) {
        loadNotes();
      }
    };

    window.addEventListener('memberNotesUpdated', handleNotesUpdated as EventListener);
    return () => {
      window.removeEventListener('memberNotesUpdated', handleNotesUpdated as EventListener);
    };
  }, [member?.uuid]);

  useEffect(() => {
    if (member?.uuid && advisorNotes.length >= 0) {
      if (advisorNotes.length > 0) {
        localStorage.setItem(`member_notes_${member.uuid}`, JSON.stringify(advisorNotes));
      } else {
        localStorage.removeItem(`member_notes_${member.uuid}`);
      }
      // Dispatch custom event to notify other components that notes have been updated
      window.dispatchEvent(new CustomEvent('memberNotesUpdated', { 
        detail: { memberUuid: member.uuid } 
      }));
    }
  }, [advisorNotes, member?.uuid]);

  const handleSaveNote = () => {
    if (advisorNoteRef.current?.value.trim()) {
      const currentUserAvatar = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32"%3E%3Ccircle cx="16" cy="16" r="16" fill="%233b82f6"/%3E%3Ctext x="16" y="21" text-anchor="middle" fill="white" font-size="12" font-weight="600" font-family="Arial, sans-serif"%3ESJ%3C/text%3E%3C/svg%3E';
      const currentUserName = 'Sarah Johnson';
      
      const newNote: Note = {
        id: Date.now().toString(),
        text: advisorNoteRef.current.value.trim(),
        author: currentUserName,
        avatar: currentUserAvatar,
        timestamp: new Date()
      };
      setAdvisorNotes(prev => [newNote, ...prev]);
      advisorNoteRef.current.value = '';
      setShowNotesHistory(true);
    }
  };

  return (
    <div>
      <textarea
        className="w-full bg-slate-50 border border-slate-200 rounded p-3 text-slate-700 text-sm mb-3 placeholder-slate-400"
        placeholder="Add notes about this member..."
        rows={3}
        ref={advisorNoteRef}
      />
      <div className="grid grid-cols-4 gap-2">
        <button className="bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs px-3 py-2 rounded transition-colors" onClick={handleSaveNote}>Save Note</button>
        <button className="bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs px-3 py-2 rounded transition-colors" onClick={() => setShowNotesHistory(s => !s)}>
          {showNotesHistory ? 'Hide History' : 'View History'}
        </button>
        <button className="bg-brand-blue hover:bg-brand-blue/90 text-white text-xs px-3 py-2 rounded transition-colors" onClick={onAssist}>Assist Member</button>
        <button className="bg-brand-purple hover:bg-brand-purple/90 text-white text-xs px-3 py-2 rounded transition-colors" onClick={onEmail}>Send Email</button>
      </div>

      {showNotesHistory && (
        <div className="mt-4 bg-slate-50 p-3 rounded-lg border border-slate-200">
          <h4 className="text-sm font-medium text-slate-700 mb-2">Notes History</h4>
          {advisorNotes.length > 0 ? (
            <div className="space-y-3">
              {advisorNotes.map((note) => (
                <div key={note.id} className="border-b border-slate-200 pb-2 last:border-0">
                  <div className="flex items-start space-x-2">
                    <img src={note.avatar} alt={note.author} className="w-6 h-6 rounded-full" />
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-xs font-medium text-slate-700">{note.author}</p>
                        <p className="text-xs text-slate-500">{format(note.timestamp, 'MMM d, yyyy')}</p>
                      </div>
                      <p className="text-sm text-slate-700">{note.text}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-500">No notes yet.</p>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Gets the most recent stats entry from the array, regardless of how it's sorted
 */
const getCurrentMonthStats = (stats: FinancialHealthStats[]): FinancialHealthStats | undefined => {
  if (!stats || stats.length === 0) return undefined;
  const sortedByDate = [...stats].sort((a, b) => new Date(b.month).getTime() - new Date(a.month).getTime());
  return sortedByDate[0];
};

/**
 * BUG-UI-079 FIX: Gets the most recent stats entry that has actual data for a specific key
 * Falls back to most recent entry if no entry has non-zero data
 */
const getLatestStatsWithData = <K extends keyof FinancialHealthStats>(
  stats: FinancialHealthStats[],
  key: K
): { stats: FinancialHealthStats | undefined; isFromDifferentMonth: boolean } => {
  if (!stats || stats.length === 0) return { stats: undefined, isFromDifferentMonth: false };

  const sortedByDate = [...stats].sort((a, b) => new Date(b.month).getTime() - new Date(a.month).getTime());
  const mostRecent = sortedByDate[0];

  // Check if most recent month has non-zero data
  const mostRecentValue = mostRecent?.[key];
  if (mostRecentValue !== null && mostRecentValue !== undefined && Number(mostRecentValue) !== 0) {
    return { stats: mostRecent, isFromDifferentMonth: false };
  }

  // Find the first month with actual data
  for (const entry of sortedByDate) {
    const value = entry[key];
    if (value !== null && value !== undefined && Number(value) !== 0) {
      return { stats: entry, isFromDifferentMonth: true };
    }
  }

  // No data found, return most recent
  return { stats: mostRecent, isFromDifferentMonth: false };
};

/**
 * Gets a value from the most recent stats entry
 */
const getCurrentValue = <K extends keyof FinancialHealthStats>(
  stats: FinancialHealthStats[],
  key: K,
  defaultValue?: number
): number => {
  const currentStats = getCurrentMonthStats(stats);
  return Number(currentStats?.[key] ?? defaultValue ?? 0);
};

/**
 * BUG-UI-079 FIX: Gets a value from the latest month that has actual data for that metric
 * This prevents showing $0 when current month is empty but previous months have data
 */
const getLatestValueWithData = <K extends keyof FinancialHealthStats>(
  stats: FinancialHealthStats[],
  key: K,
  defaultValue?: number
): { value: number; month: string | null; isFromDifferentMonth: boolean } => {
  const { stats: latestStats, isFromDifferentMonth } = getLatestStatsWithData(stats, key);
  return {
    value: Number(latestStats?.[key] ?? defaultValue ?? 0),
    month: latestStats?.month ?? null,
    isFromDifferentMonth
  };
};

/**
 * Gets the formatted date string for the current month's stats
 */
const getCurrentMonthFormatted = (stats: FinancialHealthStats[], fallback: string = 'N/A'): string => {
  const currentStats = getCurrentMonthStats(stats);
  if (!currentStats?.month) return fallback;
  try {
    return format(parseISO(currentStats.month), 'MMMM yyyy');
  } catch {
    return fallback;
  }
};

/**
 * BUG-UI-096 FIX: Safely format a month string (YYYY-MM or YYYY-MM-DD) to display format
 * Uses parseISO to avoid timezone issues with new Date() which parses as UTC
 * and can show the wrong month in local timezones (e.g., "2025-12-01" -> "November 2025" in MST)
 */
const formatMonthSafe = (month: string, formatStr: string = 'MMMM yyyy'): string => {
  try {
    // Normalize to YYYY-MM-DD format for parseISO
    const normalizedMonth = month.length === 7 ? `${month}-01` : month;
    return format(parseISO(normalizedMonth), formatStr);
  } catch {
    return month; // Return original if parsing fails
  }
};

/**
 * Calculates the average value for a specific key over the specified number of months
 */
const getAverageValue = <K extends keyof FinancialHealthStats>(
  stats: FinancialHealthStats[],
  key: K,
  months: number = 24,
  defaultValue?: number
): number => {
  if (!stats || !Array.isArray(stats) || stats.length === 0) return Number(defaultValue ?? 0);
  const sortedStats = [...stats].sort((a, b) => new Date(b.month).getTime() - new Date(a.month).getTime());
  const statsToAverage = sortedStats.slice(1, Math.min(months + 1, sortedStats.length));
  if (statsToAverage.length === 0) return Number(defaultValue ?? 0);
  const sum = statsToAverage.reduce((total, stat) => {
    const value = Number(stat[key] ?? 0);
    return total + (isNaN(value) ? 0 : value);
  }, 0);
  return sum / statsToAverage.length;
};

/**
 * Extract initials from member data
 * Fallback order: first_name/last_name -> member_name -> phone_number -> auth0_user_id -> '??'
 *
 * TEMP FIX: PII vault is incomplete, so most members have empty first_name/last_name
 * Using phone number or auth0 ID as last resort to avoid showing '??' for all members
 */
const getMemberInitials = (member: {
  first_name?: string;
  last_name?: string;
  member_name?: string;
  phone_number?: string;
  auth0_user_id?: string;
}): string => {
  // Demo2: avoid identical "DM" collision across all "Demo MemberN" seed users
  // Use "M" + trailing digits so each demo card has a distinct avatar label
  if (member.first_name === 'Demo' && member.last_name) {
    const digits = member.last_name.replace(/[^0-9]/g, '');
    if (digits) {
      return `M${digits.slice(-2)}`;
    }
  }

  // Try first_name + last_name
  if (member.first_name && member.last_name) {
    return `${member.first_name[0]?.toUpperCase() || ''}${member.last_name[0]?.toUpperCase() || ''}`;
  }

  // Fallback to member_name
  if (member.member_name) {
    const parts = member.member_name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return `${parts[0][0]?.toUpperCase() || ''}${parts[parts.length - 1][0]?.toUpperCase() || ''}`;
    }
    if (parts.length === 1 && parts[0]) {
      return parts[0][0]?.toUpperCase() || '?';
    }
  }

  // TEMP: Use last 2 digits of phone number as initials (e.g., "65" for +15551234565)
  if (member.phone_number && member.phone_number.length >= 2) {
    const digits = member.phone_number.replace(/\D/g, ''); // Remove non-digits
    if (digits.length >= 2) {
      return digits.slice(-2); // Last 2 digits
    }
  }

  // TEMP: Use last 2 chars of auth0_user_id (e.g., "2b" from "auth0|6914c106c521ed42b809c62b")
  if (member.auth0_user_id) {
    const id = member.auth0_user_id.replace('auth0|', '');
    if (id.length >= 2) {
      return id.slice(-2).toUpperCase();
    }
  }

  return '??';
};

const MemberCard: React.FC<MemberCardProps> = ({ member, financialData, globalMonth, onAssist, onEmail }) => {
  // DEBUG: Check if run_id is present
  console.log(`🔍 [MemberCard] ${member.member_name} - run_id: "${member.run_id || 'MISSING'}", uuid: ${member.uuid}`);
  // DEBUG: Trace globalMonth prop value
  console.log(`📅 [MemberCard] ${member.member_name} - globalMonth prop: "${globalMonth}"`);


  // Check if member needs onboarding (critical for data gating)
  const isOnboarding = needsOnboardingOverlay(member.dashboard_status);

  // Feature flag for VeraScore vs Credit Score comparison
  const [showScoreComparison, setShowScoreComparison] = useState(true);
  // Use financialData prop directly from API - no more direct Supabase queries
  const [stats, setStats] = useState<FinancialHealthStats[]>(financialData || []);
  const [isLoading, setIsLoading] = useState(false); // No async fetch needed - data comes from prop
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState<'predict' | 'decide' | 'connect'>('predict');

  // Option 3: Per-card month override (only used when expanded)
  const [cardSelectedMonth, setCardSelectedMonth] = useState<string | null>(null);

  // Reset card-specific month selection when card collapses
  useEffect(() => {
    if (!isExpanded) {
      setCardSelectedMonth(null);
    }
  }, [isExpanded]);

  // Get the effective month for this card: card override > global month > latest
  const effectiveMonth = useMemo((): string | null => {
    // When expanded with a card-specific selection, use that
    if (isExpanded && cardSelectedMonth) {
      console.log(`📅 [MemberCard] effectiveMonth: using cardSelectedMonth="${cardSelectedMonth}"`);
      return cardSelectedMonth;
    }
    // Otherwise use the global month from page level (convert undefined to null)
    const result = globalMonth ?? null;
    console.log(`📅 [MemberCard] effectiveMonth: using globalMonth="${globalMonth}" -> result="${result}"`);
    return result;
  }, [isExpanded, cardSelectedMonth, globalMonth]);

  // Helper to get stats for a specific month
  const getStatsForMonth = useCallback((month: string | null): FinancialHealthStats | undefined => {
    if (!stats || stats.length === 0) return undefined;
    if (!month) {
      // No month specified, return most recent
      const sorted = [...stats].sort((a, b) => b.month.localeCompare(a.month));
      return sorted[0];
    }
    // Find exact month match
    const found = stats.find(s => s.month === month);
    if (found) return found;
    // Fallback to most recent if month not found
    const sorted = [...stats].sort((a, b) => b.month.localeCompare(a.month));
    return sorted[0];
  }, [stats]);

  // Get the stats for the effective month (used for consistent display)
  const monthStats = useMemo(() => getStatsForMonth(effectiveMonth), [getStatsForMonth, effectiveMonth]);

  // Option 3: Hybrid Date Selection - effectiveStats provides selected month data with fallback
  const effectiveStats = useMemo(() => monthStats || stats[0], [monthStats, stats]);

  // State for lazy-loaded detailed history (Score Details)
  const [detailedHistory, setDetailedHistory] = useState<MemberHistoryPoint[] | null>(null);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const historyFetchedRef = useRef(false); // Prevent duplicate fetches

  // State for editing member profile in 360 View (per-field editing)
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editedMember, setEditedMember] = useState<Member>(member);
  const [isSavingField, setIsSavingField] = useState(false);


  // Sync editedMember when member prop changes
  useEffect(() => {
    setEditedMember(member);
  }, [member]);

  // BUG-UI-100/104/106 FIX: Fetch factor scores from algo_run_evidence to ensure
  // dashboard and modal show the same values. The lender_member_summary view reads
  // from verascores.verascore JSONB, but modals read from algo_run_evidence.intermediate_metrics.
  // These two sources can have different values if the verascores table wasn't updated.
  // By fetching from the audit evidence API using the run_id, we get the same factor scores
  // that the modal will display.
  const factorsFetchedRef = useRef(false);
  useEffect(() => {
    // Fetch factor scores on mount (only once) using run_id from member
    if (!factorsFetchedRef.current && member.run_id) {
      factorsFetchedRef.current = true;
      const fetchFactorScores = async () => {
        try {
          // Fetch FACTOR evidence records from algo_run_evidence
          const evidenceResponse = await getRunEvidence(member.run_id!, {
            entity_type: 'FACTOR',
          });

          if (evidenceResponse.evidence && evidenceResponse.evidence.length > 0) {
            // Extract factor scores from evidence records
            const factorScores: Record<string, number | null> = {};
            for (const record of evidenceResponse.evidence) {
              const score = record.intermediate_metrics?.score;
              if (typeof score === 'number') {
                // Map entity_name to factor key
                // e.g., 'cash_flow_factor' -> 'cash_flow', 'debt_to_income_factor' -> 'debt_to_income'
                const factorKey = record.entity_name?.replace('_factor', '');
                if (factorKey) {
                  factorScores[factorKey] = score;
                }
              }
            }

            // Update stats with factor scores from audit evidence
            setStats(prevStats => {
              if (prevStats.length === 0) {
                // No existing stats, create new entry with factor scores
                return [{
                  month: new Date().toISOString().slice(0, 7), // Current month
                  factor_dti: factorScores['debt_to_income'] ?? null,
                  factor_cash_on_hand: factorScores['liquid_reserves'] ?? null,
                  factor_spending: factorScores['cash_flow'] ?? null,
                  factor_savings: factorScores['savings'] ?? null,
                  factor_debt: factorScores['debt'] ?? factorScores['debt_management'] ?? null,
                  factor_payment_history: factorScores['payment_history'] ?? null,
                } as FinancialHealthStats];
              }
              // Update existing stats with factor scores from audit evidence
              return prevStats.map((stat, index) => {
                if (index === 0) {
                  return {
                    ...stat,
                    factor_dti: factorScores['debt_to_income'] ?? stat.factor_dti,
                    factor_cash_on_hand: factorScores['liquid_reserves'] ?? stat.factor_cash_on_hand,
                    factor_spending: factorScores['cash_flow'] ?? stat.factor_spending,
                    factor_savings: factorScores['savings'] ?? stat.factor_savings,
                    factor_debt: factorScores['debt'] ?? factorScores['debt_management'] ?? stat.factor_debt,
                    factor_payment_history: factorScores['payment_history'] ?? stat.factor_payment_history,
                  };
                }
                return stat;
              });
            });
            console.log(`✅ [MemberCard] BUG-UI-100/104/106 FIX: Updated factor scores from audit evidence for ${member.member_name}:`, factorScores);
          }
        } catch (err) {
          console.warn('[MemberCard] Failed to fetch factor scores from audit evidence, using fallback from financialData:', err);
          // Don't block UI - fallback to financialData prop values
        }
      };
      fetchFactorScores();
    }
  }, [member.run_id, member.member_name]);

  // BUG-UI-131 FIX Phase 3: Fetch payment history evidence for late payments trend chart
  // The getMemberHistory() API does NOT include number_of_late_payments in its response.
  // The authoritative source is the audit evidence API (payment_history_factor entity)
  // which contains 24 monthly records with intermediate_metrics.number_of_late_payments.
  // This state stores the monthly late payment data from evidence API.
  const [latePaymentsEvidence, setLatePaymentsEvidence] = useState<Record<string, number>>({});
  const latePaymentsEvidenceFetchedRef = useRef(false);

  useEffect(() => {
    // Fetch payment history evidence when card expands (for chart trend data)
    if (isExpanded && !latePaymentsEvidenceFetchedRef.current && member.run_id) {
      latePaymentsEvidenceFetchedRef.current = true;
      const fetchLatePaymentsEvidence = async () => {
        try {
          // Fetch payment_history_factor evidence records (24 months)
          const evidenceResponse = await getRunEvidence(member.run_id!, {
            entity_name: 'payment_history_factor',
          });

          if (evidenceResponse.evidence && evidenceResponse.evidence.length > 0) {
            // Extract number_of_late_payments per month from evidence records
            const monthlyLatePayments: Record<string, number> = {};
            for (const record of evidenceResponse.evidence) {
              const monthKey = record.month_key;
              const latePayments = record.intermediate_metrics?.number_of_late_payments;
              if (monthKey && typeof latePayments === 'number') {
                monthlyLatePayments[monthKey] = latePayments;
              }
            }

            setLatePaymentsEvidence(monthlyLatePayments);
            console.log(`✅ [MemberCard] BUG-UI-131 FIX: Fetched late payments evidence for ${member.member_name}:`, monthlyLatePayments);

            // Merge late payments data into stats array
            setStats(prevStats => {
              if (prevStats.length === 0) return prevStats;
              return prevStats.map(stat => {
                const latePaymentsForMonth = monthlyLatePayments[stat.month];
                if (latePaymentsForMonth !== undefined) {
                  return {
                    ...stat,
                    number_of_late_payments: latePaymentsForMonth,
                  };
                }
                return stat;
              });
            });
          }
        } catch (err) {
          console.warn('[MemberCard] Failed to fetch late payments evidence:', err);
          // Don't block UI - chart will show whatever data is available
        }
      };
      fetchLatePaymentsEvidence();
    }
  }, [isExpanded, member.run_id, member.member_name]);

  // Lazy load detailed history when Score Details is expanded
  useEffect(() => {
    // Use ref to prevent duplicate fetches even across re-renders
    if (isExpanded && !historyFetchedRef.current && !isLoadingHistory) {
      historyFetchedRef.current = true; // Mark as fetched immediately
      const fetchHistory = async () => {
        setIsLoadingHistory(true);
        setHistoryError(null);
        try {
          const response = await getMemberHistory(member.uuid, {
            months: 24,
            includeFactors: true,
            includeInputs: true,
          });
          setDetailedHistory(response.series);
          // Map history to stats format for charts
          if (response.series && response.series.length > 0) {
            const mappedStats = response.series.map((point) => ({
              month: point.month, // Use 'month' to match FinancialHealthStats interface
              verascore: point.score,
              verascore_health: point.rating,
              yearly_gross_income: point.inputs?.yearly_gross_income ?? null,
              gross_income: point.inputs?.gross_income ?? null,
              minimum_debt_payments: point.inputs?.minimum_debt_payments ?? null,
              liquid_reserves: point.inputs?.liquid_reserves ?? null,
              monthly_expenses: point.inputs?.monthly_expenses ?? null,
              money_in: point.inputs?.gross_income ?? null, // money_in = gross_income (monthly)
              money_leftover: point.inputs?.money_leftover ?? null,
              net_savings: point.inputs?.net_savings ?? null,
              total_outstanding_adverse_debt: point.inputs?.outstanding_debt ?? null,
              // BUG-UI-077 FIX: Calculate dti_ratio if not provided by backend
              // Formula: dti_ratio = (minimum_debt_payments / monthly_gross_income) * 100
              dti_ratio: point.inputs?.dti_ratio ??
                (point.inputs?.gross_income && point.inputs.gross_income > 0 && point.inputs?.minimum_debt_payments
                  ? (point.inputs.minimum_debt_payments / point.inputs.gross_income) * 100
                  : null),
              factor_dti: point.factors?.debt_to_income ?? null,
              factor_cash_on_hand: point.factors?.liquid_reserves ?? null,
              factor_spending: point.factors?.cash_flow ?? null,
              factor_savings: point.factors?.savings ?? null,
              factor_debt: point.factors?.debt ?? null,
              factor_payment_history: point.factors?.payment_history ?? null,
            } as Partial<FinancialHealthStats>));
            setStats(mappedStats as FinancialHealthStats[]);

            // DEMO2 graph restoration: when /history.series is degenerate (single
            // current/restated row), synthesize the missing 24 months from
            // algo_run_evidence so the expanded-card trend charts can render.
            // Display-only; the latest official month from /history is preserved
            // via overlayLatestMonth(). Fail-soft: helper returns [] on error.
            if (response.series.length < 3 && member.run_id) {
              try {
                // Phase 14: pass the current /history anchor month + member id
                // so the fallback shifts its 24-month series forward to end at
                // the Demo2 demo window when evidence trails the score_month.
                const anchorMonth = (mappedStats[0] as FinancialHealthStats | undefined)?.month ?? null;
                const fallback = await buildFallbackHistoryFromEvidence(member.run_id, {
                  anchorMonth,
                  memberId: member.uuid,
                });
                if (fallback.length >= 3) {
                  const merged = overlayLatestMonth(
                    fallback,
                    mappedStats[0] as FinancialHealthStats
                  );
                  setStats(merged);
                }
              } catch (fallbackErr) {
                console.warn('[MemberCard] DEMO2 evidence fallback failed:', fallbackErr);
              }
            }
          }
        } catch (err) {
          console.error('[MemberCard] Error fetching detailed history:', err);
          setHistoryError(err instanceof Error ? err.message : 'Failed to load history');
          historyFetchedRef.current = false; // Allow retry on error
        } finally {
          setIsLoadingHistory(false);
        }
      };
      fetchHistory();
    }
  }, [isExpanded, isLoadingHistory, member.uuid]);

  // Defensive casts in case constants have a slightly different compile-time type
  const initialFactor = (FACTORS.find((f: any) => f.key === 'verascore') || FACTORS[0]) as unknown as Factor;
  const [activeFactor, setActiveFactor] = useState<Factor>(initialFactor);

  const initialMetric = ((FINANCIAL_METRICS && FINANCIAL_METRICS[0]) || {
    key: 'gross_income',
    label: 'Gross Income',
    format: 'currency',
    description: 'Yearly gross income',
  }) as unknown as FinancialMetric;
  const [selectedMetric, setSelectedMetric] = useState<FinancialMetric>(initialMetric);

  const { showDebug } = useDebug();
  // BUG FIX (Dec 18, 2025): Use historical scores from member object (from lender_member_summary view)
  // instead of calculating averages from financial_health_stats_v2.
  // The member object comes from the API which queries lender_member_summary view
  // which already has the correct six_month_score, twelve_month_score, twenty_four_month_score
  // calculated and stored in the verascores table.
  const scoreMetrics = useMemo(() => {
    // Option 3: When a specific month is selected, use that month's VeraScore
    if (effectiveMonth && effectiveStats && effectiveStats.verascore !== undefined && effectiveStats.verascore !== null) {
      console.log('[MemberCard] Using effectiveStats for selected month:', {
        member_name: member.member_name,
        selected_month: effectiveMonth,
        current_score: effectiveStats.verascore,
        // Historical averages still come from member object (pre-calculated)
        six_month_score: member.six_month_score,
        twelve_month_score: member.twelve_month_score,
        twenty_four_month_score: member.twenty_four_month_score,
      });
      return {
        current_score: effectiveStats.verascore || 0,
        six_month_avg: member.six_month_score || 0,
        twelve_month_avg: member.twelve_month_score || 0,
        twenty_four_month_avg: member.twenty_four_month_score || 0,
        trend_direction: 'stable' as const,
        health_status: 'good' as const,
      };
    }

    // Default: Use the pre-calculated scores from the database if available
    // Check if member has verascore field (from MemberWithScore type)
    if ('verascore' in member && member.verascore !== undefined && member.verascore !== null) {
      console.log('[MemberCard] Using member historical scores:', {
        member_name: member.member_name,
        current_score: member.verascore,
        six_month_score: member.six_month_score,
        twelve_month_score: member.twelve_month_score,
        twenty_four_month_score: member.twenty_four_month_score,
      });
      return {
        current_score: member.verascore || 0,
        six_month_avg: member.six_month_score || 0,
        twelve_month_avg: member.twelve_month_score || 0,
        twenty_four_month_avg: member.twenty_four_month_score || 0,
        trend_direction: 'stable' as const,
        health_status: 'good' as const,
      };
    }
    // Fallback to calculation if member doesn't have the fields (shouldn't happen with API data)
    console.log('[MemberCard] Falling back to calculateScoreMetricsFromStats for:', member.member_name);
    return calculateScoreMetricsFromStats(financialData);
  }, [member, financialData, effectiveMonth, effectiveStats]);
  // REMOVED: dataSource - no longer needed since we use API data via financialData prop
  const chartRef = useRef<any>(null);
  const financeChartRef = useRef<any>(null);
  const { isMobile } = useMobileChart();
  const { showEvidence } = useEvidenceModal();

  // Option 3: Use effectiveStats for baseline comparison based on selected month
  useVeraScoreComparison({
    baseline: {
      month: effectiveStats?.month || '',
      gross_income: effectiveStats?.gross_income || null,
      minimum_debt_payments: effectiveStats?.minimum_debt_payments || null,
      liquid_reserves: effectiveStats?.liquid_reserves || null,
      monthly_expenses: effectiveStats?.monthly_expenses || null,
      money_in: effectiveStats?.money_in || null,
      money_leftover: effectiveStats?.money_leftover || null,
      net_savings: effectiveStats?.net_savings || null,
      total_outstanding_adverse_debt: effectiveStats?.total_outstanding_adverse_debt || null,
      actual_adverse_debt_payments: effectiveStats?.actual_adverse_debt_payments || null,
      number_of_late_payments: effectiveStats?.number_of_late_payments || null,
      age_most_recent_late: effectiveStats?.age_most_recent_late || null,
      verascore: effectiveStats?.verascore || null,
      verascore_health: effectiveStats?.verascore_health || null,
      factor_dti: effectiveStats?.factor_dti || null,
      dti_ratio: effectiveStats?.dti_ratio || null,
      dti_health: effectiveStats?.dti_health || null,
      factor_cash_on_hand: effectiveStats?.factor_cash_on_hand || null,
      cash_lr_ratio: effectiveStats?.cash_lr_ratio || null,
      cash_lr_health: effectiveStats?.cash_lr_health || null,
      factor_spending: effectiveStats?.factor_spending || null,
      spending_cf_ratio: effectiveStats?.spending_cf_ratio || null,
      spending_cf_health: effectiveStats?.spending_cf_health || null,
      factor_savings: effectiveStats?.factor_savings || null,
      savings_s_ratio: effectiveStats?.savings_s_ratio || null,
      savings_s_health: effectiveStats?.savings_s_health || null,
      factor_debt: effectiveStats?.factor_debt || null,
      debt_ratio: effectiveStats?.debt_ratio || null,
      debt_health: effectiveStats?.debt_health || null,
      factor_payment_history: effectiveStats?.factor_payment_history || null,
      payment_crossing: effectiveStats?.payment_crossing || null,
      payment_history_late_payment_factor: effectiveStats?.payment_history_late_payment_factor || null,
      payment_history_health: effectiveStats?.payment_history_health || null
    },
    calculatedStats: stats[stats.length - 1] || {
      month: '',
      gross_income: null,
      minimum_debt_payments: null,
      liquid_reserves: null,
      monthly_expenses: null,
      money_in: null,
      money_leftover: null,
      net_savings: null,
      total_outstanding_adverse_debt: null,
      actual_adverse_debt_payments: null,
      number_of_late_payments: null,
      age_most_recent_late: null,
      verascore: null,
      verascore_health: null,
      factor_dti: null,
      dti_ratio: null,
      dti_health: null,
      factor_cash_on_hand: null,
      cash_lr_ratio: null,
      cash_lr_health: null,
      factor_spending: null,
      spending_cf_ratio: null,
      spending_cf_health: null,
      factor_savings: null,
      savings_s_ratio: null,
      savings_s_health: null,
      factor_debt: null,
      debt_ratio: null,
      debt_health: null,
      factor_payment_history: null,
      payment_crossing: null,
      payment_history_late_payment_factor: null,
      payment_history_health: null
    }
  });

  // Use financialData prop from API for initial stats (before history is loaded)
  // Skip if detailedHistory is already loaded (it has richer data with inputs)
  useEffect(() => {
    if (financialData && financialData.length > 0 && !detailedHistory) {
      const sortedData = [...financialData].sort((a, b) =>
        new Date(b.month).getTime() - new Date(a.month).getTime()
      );
      setStats(sortedData);
      // ensure metric is set (safe even if FINANCIAL_METRICS changes)
      setSelectedMetric(((FINANCIAL_METRICS && FINANCIAL_METRICS[0]) || initialMetric) as FinancialMetric);
      console.log(`📊 MemberCard using API data for ${member.uuid}:`, { stats: sortedData.length });
    }
  }, [financialData, member.uuid, initialMetric, detailedHistory]);

  if (isLoading) {
    return <LoadingSpinner />;
  }
  // Removed error check from useScoreData - scoreMetrics is now calculated synchronously from financialData

  return (
    <DebugLayout>
      <div className="bg-white rounded-lg shadow-sm px-3 pt-6 pb-3">
        {/* GRID 1: Header Grid */}
        <div className="grid grid-cols-3 gap-6 max-sm:grid-cols-1 max-sm:gap-4">
          {/* Section 1A: Name & Contact */}
          <DebugSection label="Section 1A: Name & Contact" className="col-span-2">
            <div className="grid grid-cols-2 gap-6 max-sm:grid-cols-1 max-sm:gap-4">
              {/* Section 1A-1: Member Info */}
              <DebugSection label="Section 1A-1: Member Info" className="col-span-1">
                {ENABLE_PHASE5_HEADER_ENHANCEMENTS ? (
                  /* Phase 5 Enhanced Header with Avatar */
                  <div className="flex items-start gap-3">
                    {/* Avatar Circle */}
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-brand-blue to-brand-purple flex items-center justify-center text-white text-lg font-bold shadow-sm flex-shrink-0">
                      {getMemberInitials(editedMember)}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="text-[28px] font-semibold max-sm:text-2xl">
                          {editedMember.first_name} {editedMember.last_name}
                        </h3>
                        {/* Star Rating */}
                        {member.verascore_rating && (
                          <span className={`text-lg ${getRatingStars(member.verascore_rating).color}`}>
                            {'★'.repeat(getRatingStars(member.verascore_rating).count)}
                            {'☆'.repeat(5 - getRatingStars(member.verascore_rating).count)}
                          </span>
                        )}
                        {/* Trend Indicator */}
                        {(() => {
                          const currentScore = stats?.verascore ?? member.currentScore ?? 0;
                          const trend = calculateTrend(currentScore, member.six_month_score);
                          if (trend.direction === 'up') {
                            return (
                              <span className="flex items-center text-green-500 text-sm font-medium">
                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                                </svg>
                                {trend.changePercent.toFixed(1)}%
                              </span>
                            );
                          } else if (trend.direction === 'down') {
                            return (
                              <span className="flex items-center text-red-500 text-sm font-medium">
                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M14.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 12.586V5a1 1 0 012 0v7.586l2.293-2.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                                {trend.changePercent.toFixed(1)}%
                              </span>
                            );
                          }
                          return null;
                        })()}
                      </div>
                      {/* Contact Links */}
                      <div className="text-brand-link text-sm max-sm:break-words">
                        <button
                          onClick={() => onEmail()}
                          className="text-brand-link hover:text-brand-link/80 hover:underline"
                        >
                          {editedMember.email}
                        </button>{' '}
                        |{' '}
                        {(() => {
                          const p = getPhoneNumber(editedMember.phone_number, editedMember.uuid);
                          return p.synthesized ? (
                            <span className="text-slate-400">{p.display}</span>
                          ) : (
                            <a
                              href={`tel:${p.dial}`}
                              className="text-brand-link hover:text-brand-link/80 hover:underline"
                            >
                              {p.display}
                            </a>
                          );
                        })()}
                      </div>
                      {/* Location - City, State */}
                      {(member.city || member.state) && (
                        <p className="text-xs text-slate-400 mt-0.5">
                          {[member.city, member.state].filter(Boolean).join(', ')}
                        </p>
                      )}
                    </div>
                  </div>
                ) : (
                  /* Original Header (pre-Phase 5) */
                  <>
                    <h3 className="text-[30px] font-semibold max-sm:text-2xl flex items-center">
                      {editedMember.first_name} {editedMember.last_name}
                    </h3>
                    <div className="text-brand-link text-sm max-sm:break-words">
                      <button
                        onClick={() => onEmail()}
                        className="text-brand-link hover:text-brand-link/80 hover:underline"
                      >
                        {editedMember.email}
                      </button>{' '}
                      |{' '}
                      {(() => {
                        const p = getPhoneNumber(editedMember.phone_number, editedMember.uuid);
                        return p.synthesized ? (
                          <span className="text-slate-400">{p.display}</span>
                        ) : (
                          <a
                            href={`tel:${p.dial}`}
                            className="text-brand-link hover:text-brand-link/80 hover:underline"
                          >
                            {p.display}
                          </a>
                        );
                      })()}
                    </div>
                  </>
                )}
              </DebugSection>

              {/* Section 1A-2: Tabs */}
              <DebugSection label="Section 1A-2: Tabs" className="col-span-1">
                <div className="flex justify-end max-sm:justify-start gap-1">
                  <button
                    className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-all ${activeTab === 'predict'
                      ? 'text-brand-purple border-b-2 border-brand-purple bg-purple-50'
                      : 'text-slate-600 hover:text-slate-800 hover:bg-slate-50'
                      }`}
                    onClick={() => setActiveTab('predict')}
                    title="View financial health metrics and VeraScore details"
                  >
                    Financial Health
                  </button>
                  <button
                    className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-all ${activeTab === 'decide'
                      ? 'text-brand-purple border-b-2 border-brand-purple bg-purple-50'
                      : 'text-slate-600 hover:text-slate-800 hover:bg-slate-50'
                      }`}
                    onClick={() => {
                      setActiveTab('decide');
                      if (isExpanded) setIsExpanded(false);
                    }}
                    title="View AI-powered insights and recommendations"
                  >
                    AI Guidance
                  </button>
                  <button
                    className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-all ${activeTab === 'connect'
                      ? 'text-brand-purple border-b-2 border-brand-purple bg-purple-50'
                      : 'text-slate-600 hover:text-slate-800 hover:bg-slate-50'
                      }`}
                    onClick={() => {
                      setActiveTab('connect');
                      if (isExpanded) setIsExpanded(false);
                    }}
                    title="View comprehensive member overview"
                  >
                    360 View
                  </button>
                </div>
              </DebugSection>
            </div>
          </DebugSection>

          {/* Section 1B: Empty section */}
          <DebugSection label="Section 1B" className="col-span-1">
            <div className="flex justify-end max-sm:justify-start">
              {/* Credit Opportunity badge */}
              {hasUnlockedCreditOpportunities(stats, member.credit_score) && (
                <div
                  className="ml-3 text-sm font-medium px-4 py-1.5 flex items-center rounded-md cursor-pointer hover:bg-opacity-80 transition-all"
                  style={{
                    backgroundColor: 'rgba(16, 185, 129, 0.1)',
                    color: '#047857',
                    borderLeft: '2px solid rgba(16, 185, 129, 0.6)',
                    boxShadow: 'none',
                    backdropFilter: 'blur(4px)'
                  }}
                  title="Click to see credit opportunities in AI Guidance"
                  onClick={() => setActiveTab('decide')}
                  role="button"
                  aria-label="View Credit Opportunities"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                  </svg>
                  <span className="font-bold pr-0.5">Credit Opportunity</span>
                </div>
              )}
            </div>
          </DebugSection>
        </div>

        {/* Spacing between grids */}
        <div className="h-6" />

        {/* Content based on active tab */}
        {activeTab === 'predict' && isOnboarding ? (
          /* Show onboarding message instead of financial data */
          <div className="bg-slate-50 rounded-lg p-12 text-center">
            <div className="max-w-md mx-auto">
              <div className="bg-indigo-100 rounded-full p-4 w-20 h-20 mx-auto mb-6 flex items-center justify-center">
                <img src={runnerIcon} alt="Onboarding" className="w-12 h-12" />
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-3">
                Onboarding In Progress
              </h3>
              <p className="text-slate-600 mb-6">
                {getOnboardingMessage(member.dashboard_status)}
              </p>
              <p className="text-sm text-slate-500 mb-8">
                Financial health data and charts will be available once the member completes onboarding.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
                <button onClick={onAssist} className="px-6 py-2.5 bg-brand-purple text-white font-medium rounded hover:bg-brand-purple/80 transition-colors text-sm sm:text-base">
                  Assist Member
                </button>
                <button onClick={onEmail} className="px-6 py-2.5 bg-blue-500 text-white font-medium rounded hover:bg-blue-600 transition-colors text-sm sm:text-base">
                  Email Member
                </button>
              </div>
            </div>
          </div>
        ) : activeTab === 'predict' ? (
          <div className="grid grid-cols-3 gap-6 max-lg:grid-cols-1 max-lg:gap-10">
              {/* Section 2A: Blue Score Panel */}
              <DebugSection label="Section 2A: Blue Score Panel" className="col-span-2 max-lg:col-span-1">
                <div className="px-3 sm:px-6 py-6 sm:py-9 rounded-lg relative overflow-hidden" style={{ backgroundColor: '#1C4583' }}>
                <div className="flex gap-4 sm:gap-6 flex-col md:flex-row">
                  {/* Section 2A-1: Main Score */}
                  <DebugSection label="Section 2A-1: Main Score" className="w-full md:w-[60%]">
                    {/* Section 2A-1-1: Status Bar */}
                    <DebugSection label="Section 2A-1-1: Status Bar" className="-mt-3 mb-4 sm:mb-6">
                      <div className="flex items-center text-white/90">
                        <span className="flex items-center gap-2">
                          <div
                            className={`w-[24px] h-[24px] rounded-full flex items-center justify-center -mt-3 ${getStatusMetadata(member.dashboard_status).iconBgColor}`}
                          >
                            <img
                              src={getStatusMetadata(member.dashboard_status).icon}
                              alt={member.dashboard_status}
                              className="w-[18px] h-[18px] invert brightness-0"
                            />
                          </div>
                          <span className="flex flex-col justify-center">
                            <span className="font-medium leading-tight">
                              {member.dashboard_status}
                            </span>
                            <span className="text-xs text-white/75 leading-tight">
                              {getStatusMetadata(member.dashboard_status).description}
                            </span>
                          </span>
                        </span>
                      </div>
                    </DebugSection>

                    {/* Section 2A-1-2: Score Display */}
                    <DebugSection label="Section 2A-1-2: Score Display" className="mb-4 mt-6 sm:mt-9">
                      <div className="grid grid-cols-[120px_1fr] sm:grid-cols-[160px_1fr] items-end gap-2">
                        {/* Score Number */}
                        <DebugSection label="Score Value">
                          <div className="text-5xl sm:text-7xl font-bold text-white text-right pr-[10%] flex items-center justify-end">
                            <div className="mr-2">
                              {scoreMetrics?.current_score && getLargeStatusIcon(Math.round(scoreMetrics.current_score))}
                            </div>
                            {scoreMetrics?.current_score ? Math.round(scoreMetrics.current_score) : '?'}
                            {/* US 7.36: Trend Arrow Indicator */}
                            {scoreMetrics?.current_score && renderTrendArrow(scoreMetrics.current_score, member.six_month_score)}
                          </div>
                        </DebugSection>

                        {/* Score Label */}
                        <DebugSection label="Score Label">
                          <div className="text-white font-bold tracking-[0.02em] text-2xl sm:text-[30px] leading-tight sm:leading-[41px]">
                            VeraScore
                          </div>
                        </DebugSection>
                      </div>
                    </DebugSection>

                    {/* Section 2A-1-3: Progress Bar */}
                    <DebugSection
                      label="Section 2A-1-3: Progress Bar"
                      className="mt-4 max-w-sm relative"
                    >
                      <div className="h-2 bg-white/20 rounded-full overflow-hidden relative">
                        <div
                          className="h-full absolute top-0 left-0 rounded-full"
                          style={{
                            width: `${scoreMetrics?.current_score || 0}%`,
                            backgroundColor: getVeraScoreColor(scoreMetrics?.current_score || 0),
                          }}
                        />
                      </div>

                      <div className="flex justify-between text-xs text-white/60 mt-1">
                        <span>0</span>
                        <span>100</span>
                      </div>
                    </DebugSection>
                  </DebugSection>

                  {/* Section 2A-2: Historical & Actions */}
                  <DebugSection
                    label="Section 2A-2: Historical & Actions"
                    className="w-[40%] relative max-md:w-full max-md:flex max-md:justify-center"
                  >
                    {isOnboarding ? (
                      <div className="absolute -top-9 -right-6 -bottom-9 -left-0 bg-[#F2F6FB] rounded-r-lg flex flex-col justify-start pt-3 pb-4 px-5 z-30 shadow-[0px_2px_10px_rgba(0,0,0,0.1)] pointer-events-auto">
                        <div className="absolute inset-0 rounded-r-lg border border-gray-200 max-md:rounded-lg" style={{ zIndex: 1 }} />
                        <DebugSection label="Onboarding Overlay" className="h-full flex flex-col justify-between relative z-[2]">
                          <DebugSection label="Onboarding Icon & Message" className="text-center mt-2">
                            <div className="flex justify-center">
                              <div className="bg-indigo-100 rounded-full p-1 mb-2 mt-0">
                                <img src={runnerIcon} alt="Onboarding" className="w-14 h-14" />
                              </div>
                            </div>
                            <p className="text-slate-800 text-sm font-medium">
                              {getOnboardingMessage(member.dashboard_status)}
                            </p>
                          </DebugSection>
                          <DebugSection label="Onboarding Actions" className="space-y-3 w-full flex flex-col items-center">
                            <button onClick={onAssist} className="w-[85%] sm:w-[80%] py-2 sm:py-1.5 bg-brand-purple text-white font-medium rounded hover:bg-brand-purple/80 text-sm sm:text-[13px] transition-colors">
                              Assist Member
                            </button>
                            <button onClick={onEmail} className="w-[85%] sm:w-[80%] py-2 sm:py-1.5 bg-blue-500 text-white font-medium rounded hover:bg-blue-600 text-sm sm:text-[13px] transition-colors">
                              Email Member
                            </button>
                          </DebugSection>
                        </DebugSection>
                      </div>
                    ) : (
                      <>
                        <DebugSection label="Section 2A-2-1: Status" className="mb-6">
                          <div className="text-right max-md:text-left" />
                        </DebugSection>

                        <DebugSection label="Section 2A-2: Historical & Actions Combined" className="mb-6 md:mb-8 max-md:w-full max-md:flex max-md:justify-center">
                          <div className="flex flex-row items-center gap-3 sm:gap-4 md:gap-8">
                              {/* Historical Scores */}
                              <div className="flex flex-col gap-2 sm:gap-2.5 md:gap-3">
                                <DebugSection label="6 Month" className="text-center">
                                  <div className="flex items-center justify-center gap-1 sm:gap-2">
                                    <div className="text-lg sm:text-xl md:text-2xl font-bold text-white">
                                      {scoreMetrics?.six_month_avg ? Math.round(scoreMetrics.six_month_avg) : '—'}
                                    </div>
                                    <div className="scale-75 sm:scale-90 md:scale-100">
                                      {scoreMetrics?.six_month_avg ? getStatusIcon(scoreMetrics.six_month_avg) : null}
                                    </div>
                                  </div>
                                  {/* Demo2 fast-fix: show delta = current - historical when both present */}
                                  {renderHistoricalDelta(scoreMetrics?.current_score, scoreMetrics?.six_month_avg)}
                                  {/* Option 3: Tooltip explaining these are current rolling averages */}
                                  <div
                                    className="text-white/75 text-[10px] sm:text-xs md:text-sm mt-0.5 cursor-help"
                                    title="This is the current rolling average as of the most recent date for which we have data."
                                  >
                                    Last 6 Months
                                  </div>
                                </DebugSection>

                                <DebugSection label="12 Month" className="text-center">
                                  <div className="flex items-center justify-center gap-1 sm:gap-2">
                                    <div className="text-lg sm:text-xl md:text-2xl font-bold text-white">
                                      {scoreMetrics?.twelve_month_avg ? Math.round(scoreMetrics.twelve_month_avg) : '—'}
                                    </div>
                                    <div className="scale-75 sm:scale-90 md:scale-100">
                                      {scoreMetrics?.twelve_month_avg ? getStatusIcon(scoreMetrics.twelve_month_avg) : null}
                                    </div>
                                  </div>
                                  {renderHistoricalDelta(scoreMetrics?.current_score, scoreMetrics?.twelve_month_avg)}
                                  {/* Option 3: Tooltip explaining these are current rolling averages */}
                                  <div
                                    className="text-white/75 text-[10px] sm:text-xs md:text-sm mt-0.5 cursor-help"
                                    title="This is the current rolling average as of the most recent date for which we have data."
                                  >
                                    Last 12 Months
                                  </div>
                                </DebugSection>

                                <DebugSection label="24 Month" className="text-center">
                                  <div className="flex items-center justify-center gap-1 sm:gap-2">
                                    <div className="text-lg sm:text-xl md:text-2xl font-bold text-white">
                                      {scoreMetrics?.twenty_four_month_avg ? Math.round(scoreMetrics.twenty_four_month_avg) : '—'}
                                    </div>
                                    <div className="scale-75 sm:scale-90 md:scale-100">
                                      {scoreMetrics?.twenty_four_month_avg ? getStatusIcon(scoreMetrics.twenty_four_month_avg) : null}
                                    </div>
                                  </div>
                                  {renderHistoricalDelta(scoreMetrics?.current_score, scoreMetrics?.twenty_four_month_avg)}
                                  {/* Option 3: Tooltip explaining these are current rolling averages */}
                                  <div
                                    className="text-white/75 text-[10px] sm:text-xs md:text-sm mt-0.5 cursor-help"
                                    title="This is the current rolling average as of the most recent date for which we have data."
                                  >
                                    Last 24 Months
                                  </div>
                                </DebugSection>
                              </div>

                              {/* Action Buttons */}
                              <DebugSection label="Actions" className="flex flex-col gap-2 sm:gap-3">
                                <button onClick={onAssist} className="w-full min-w-[100px] sm:min-w-[120px] md:min-w-[140px] py-2 sm:py-2.5 md:py-2 bg-brand-purple text-white font-medium rounded hover:bg-brand-purple/80 text-xs sm:text-sm md:text-[13px] transition-colors whitespace-nowrap px-2 sm:px-3">
                                  Assist Member
                                </button>
                                <button onClick={onEmail} className="w-full min-w-[100px] sm:min-w-[120px] md:min-w-[140px] py-2 sm:py-2.5 md:py-2 bg-blue-500 text-white font-medium rounded hover:bg-blue-600 text-xs sm:text-sm md:text-[13px] transition-colors whitespace-nowrap px-2 sm:px-3">
                                  Email Member
                                </button>
                              </DebugSection>
                            </div>
                        </DebugSection>
                      </>
                    )}
                  </DebugSection>
                </div>
              </div>
            </DebugSection>

            {/* Section 2B: Member Stats Panel */}
            <DebugSection label="Section 2B: Member Stats" className="col-span-1 flex items-center mt-3 md:mt-0">
              <DebugSection label="Section 2B-2: Stats Grid" className="w-full">
                <div className="grid grid-cols-2 gap-x-6 gap-y-7 max-sm:grid-cols-1">
                  <DebugSection label="Income">
                    <div>
                      {/* VS-BUG-001: Renamed "Yearly Gross Income" → "Annual Deposited Income" for audit accuracy */}
                      <div className="text-slate-500">
                        Annual Deposited Income
                        {/* BUG-UI-062: Removed income basis badge - info is available in the modal */}
                      </div>
                      {/* BUG-UI-029 FIX: Use yearly_gross_income mapping to open yearly metric modal */}
                      {/* BUG-UI-062 FIX: Display DEPOSITED income (verifiable), not attested */}
                      {/* Option 3: Use effectiveStats for selected month */}
                      <ClickableMetric
                        uiFieldPath="lender.current_finances.yearly_gross_income"
                        runId={member.run_id || ''}
                        memberId={member.uuid}
                        memberName={member.member_name}
                        disabled={isOnboarding}
                        incomeBasis={effectiveStats?.income_basis}
                        initialMonth={effectiveMonth}
                      >
                        <div className="text-2xl font-medium max-sm:text-xl">
                          {(() => {
                            if (isOnboarding) return '—';
                            // Demo2 fast-fix: only render currency when a numeric source is present.
                            // Fallback order: deposited_income -> income_basis.deposited_net ->
                            // yearly_gross_income -> '—'. Never coalesce missing fields to $0.00.
                            const depositedFromSummary = member.deposited_income;
                            const depositedFromStats = effectiveStats?.income_basis?.income_sources?.deposited_net;
                            const scoredIncome = member.yearly_gross_income;
                            const displayIncome = depositedFromSummary ?? depositedFromStats ?? scoredIncome;
                            return displayIncome == null ? '—' : formatCurrency(displayIncome);
                          })()}
                        </div>
                      </ClickableMetric>
                    </div>
                  </DebugSection>

                  <DebugSection label="Credit">
                    <div>
                      <div className="text-slate-500">Credit Score</div>
                      {/* Demo2 fast-fix: credit_scores table is empty for Demo CU. When a
                          synthetic value is present on member.credit_score (DEMO_SYNTHETIC
                          via demo2PersonaOverlay) display it; otherwise keep "Not Pulled". */}
                      {(() => {
                        const hasCreditScore =
                          typeof member.credit_score === 'number' && member.credit_score > 0;
                        return (
                          <ClickableMetric
                            uiFieldPath="lender.current_finances.credit_score"
                            runId={member.run_id || ''}
                            memberId={member.uuid}
                            memberName={member.member_name}
                            disabled={isOnboarding}
                            initialMonth={effectiveMonth}
                            dashboardValue={{
                              value: hasCreditScore ? (member.credit_score as number) : 0,
                              formattedValue: hasCreditScore
                                ? String(member.credit_score)
                                : 'Not Pulled',
                              unit: 'COUNT',
                            }}
                          >
                            <div
                              className={`text-2xl font-medium max-sm:text-xl ${hasCreditScore ? '' : 'text-slate-400'}`}
                            >
                              {isOnboarding
                                ? '—'
                                : hasCreditScore
                                  ? member.credit_score
                                  : 'Not Pulled'}
                            </div>
                          </ClickableMetric>
                        );
                      })()}
                    </div>
                  </DebugSection>

                  <DebugSection label="VeraScore" className="mt-3">
                    <div>
                      {/* BUG-UI-128 FIX: Label shows "VeraScore" with month below */}
                      <div className="text-slate-500">VeraScore</div>
                      {/* Click-to-Explain: VeraScore evidence */}
                      <ClickableMetric
                        uiFieldPath="lender.verascore.current"
                        runId={member.run_id || ''}
                        memberId={member.uuid}
                        memberName={member.member_name}
                        disabled={isOnboarding}
                        initialMonth={effectiveMonth}
                        tooltipText="Most recent month for which data is available. Click to see more about VeraScore."
                      >
                        <div className="text-xl font-medium">
                          {/* Option 3: Show selected month's date when a month is selected */}
                          {/* BUG-UI-096 FIX: Use formatMonthSafe to avoid timezone issues */}
                          {isOnboarding ? '—' : effectiveMonth
                            ? formatMonthSafe(effectiveMonth)
                            : getCurrentMonthFormatted(stats)}
                        </div>
                      </ClickableMetric>
                    </div>
                  </DebugSection>

                  <DebugSection label="Score Details" className="mt-3">
                    <div className="flex items-start">
                      <button
                        onClick={() => {
                          const newExpanded = !isExpanded;
                          setIsExpanded(newExpanded);
                          // Audit traceability: record when lender expands for detailed view
                          if (newExpanded) {
                            recordMemberExpanded(member.uuid);
                          }
                        }}
                        className="text-blue-600 hover:text-blue-700 flex items-center gap-1"
                      >
                        Score Details
                        <svg className={`w-5 h-5 transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                    </div>
                  </DebugSection>
                </div>
              </DebugSection>
            </DebugSection>
          </div>
        ) : activeTab === 'decide' ? (
          /* --- Decide Tab --- (unchanged content from your original, aside from safe imports/props) */
          <div className="mt-6">
            {/* Factor Analysis Section */}
            <DebugSection label="Factor Analysis" className="col-span-full">
              <div className="grid grid-cols-3 gap-6 max-lg:grid-cols-1">
                {/* Analysis Panel */}
                <div
                  className="col-span-2 rounded-xl p-6 max-lg:col-span-1 relative overflow-hidden"
                  style={{
                    background: 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    boxShadow: '0 8px 32px rgba(31, 38, 135, 0.1)'
                  }}
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-brand-blue/5 to-brand-purple/5 pointer-events-none" />
                  <DebugSection label="Analysis">
                    {(() => {
                      const defaultFactor = (FACTORS.find((f: any) => f.key === 'verascore') || FACTORS[0]) as unknown as Factor;
                      const analysis = getFactorAnalysis(
                        defaultFactor,
                        stats,
                        Number(stats[stats.length - 1]?.[defaultFactor.key]) || 0,
                        member
                      );
                      return (
                        <div className="space-y-6 relative z-10">
                          {/* Toggle button for easy revert capability */}
                          <div className="flex justify-end mb-4">
                            <button 
                              onClick={() => setShowScoreComparison(!showScoreComparison)}
                              className={`px-3 py-1 text-sm rounded-md transition-all ${
                                showScoreComparison 
                                  ? 'bg-emerald-100 text-emerald-700 border border-emerald-200' 
                                  : 'bg-slate-100 text-slate-600 border border-slate-200'
                              }`}
                              title={`${showScoreComparison ? 'Hide' : 'Show'} VeraScore vs Credit comparison`}
                            >
                              {showScoreComparison ? '✓ Score Comparison' : 'Show Score Comparison'}
                            </button>
                          </div>
                          
                          {/* VeraScore vs Credit Score Comparison - Restored with toggle */}
                          {showScoreComparison && (
                            <ScoreComparisonCard
                              member={member}
                              stats={stats}
                              getCurrentValue={getCurrentValue}
                              getMockCreditScore={getMockCreditScore}
                              hasUnlockedCreditOpportunities={hasUnlockedCreditOpportunities}
                            />
                          )}
                          <div
                            className="mb-2 pb-2"
                            style={{ borderBottom: '1px solid rgba(203, 213, 225, 0.3)' }}
                          >
                            <h3 className="text-xl font-semibold text-slate-800 max-sm:text-lg">
                              Trend Analysis
                            </h3>
                          </div>
                          <p className="text-slate-600 max-sm:text-sm">
                            {analysis.trendAnalysis.text}
                          </p>
                          {analysis.trendAnalysis.indicators?.length ? (
                            <ul
                              className="mt-3 space-y-1 text-slate-600 max-sm:text-sm p-3 rounded-lg"
                              style={{
                                background: 'linear-gradient(to bottom, rgba(248, 250, 252, 0.8), rgba(238, 242, 247, 0.8))',
                                border: '1px solid rgba(203, 213, 225, 0.3)',
                                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                              }}
                            >
                              {analysis.trendAnalysis.indicators.map((indicator: string, i: number) => (
                                <li key={i} className="flex items-start">
                                  <span className="text-brand-blue mr-2">•</span>
                                  {indicator}
                                </li>
                              ))}
                            </ul>
                          ) : null}

                          <div
                            className="mb-2 pb-2"
                            style={{ borderBottom: '1px solid rgba(203, 213, 225, 0.3)' }}
                          >
                            <h3 className="text-xl font-semibold text-slate-800 max-sm:text-lg">
                              Risk Assessment
                            </h3>
                          </div>
                          <p
                            className="text-slate-600 max-sm:text-sm p-3 rounded-lg"
                            style={{
                              background: analysis.riskAssessment.level === 'low'
                                ? 'linear-gradient(to right, rgba(209, 250, 229, 0.3), rgba(167, 243, 208, 0.3))'
                                : analysis.riskAssessment.level === 'medium'
                                  ? 'linear-gradient(to right, rgba(254, 240, 138, 0.3), rgba(253, 224, 71, 0.3))'
                                  : 'linear-gradient(to right, rgba(254, 202, 202, 0.3), rgba(252, 165, 165, 0.3))',
                              border: analysis.riskAssessment.level === 'low'
                                ? '1px solid rgba(110, 231, 183, 0.3)'
                                : analysis.riskAssessment.level === 'medium'
                                  ? '1px solid rgba(253, 224, 71, 0.3)'
                                  : '1px solid rgba(252, 165, 165, 0.3)',
                              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
                            }}
                          >
                            {analysis.riskAssessment.text}
                          </p>

                          <div
                            className="mb-2 pb-2"
                            style={{ borderBottom: '1px solid rgba(203, 213, 225, 0.3)' }}
                          >
                            <h3 className="text-xl font-semibold text-slate-800 max-sm:text-lg">
                              Recommendations
                            </h3>
                          </div>
                          <div className="space-y-4">
                            {analysis.recommendations.loanProducts.length > 0 && (
                              <div
                                className="p-4 rounded-lg"
                                style={{
                                  background: 'linear-gradient(to right, rgba(254, 240, 138, 0.3), rgba(253, 224, 71, 0.3))',
                                  border: '1px solid rgba(253, 224, 71, 0.3)',
                                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
                                }}
                              >
                                <h4 className="font-medium text-slate-700 mb-2 max-sm:text-base">Loan Products</h4>
                                <ul className="space-y-1 text-slate-600 max-sm:text-sm">
                                  {analysis.recommendations.loanProducts.map((product: string, i: number) => (
                                    <li key={i} className="flex items-start">
                                      <span className="text-brand-blue mr-2">•</span>
                                      {product}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {analysis.recommendations.advisoryPoints.length > 0 && (
                              <div
                                className="p-4 rounded-lg"
                                style={{
                                  background: 'linear-gradient(to right, rgba(224, 242, 254, 0.3), rgba(186, 230, 253, 0.3))',
                                  border: '1px solid rgba(186, 230, 253, 0.3)',
                                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
                                }}
                              >
                                <h4 className="font-medium text-slate-700 mb-2 max-sm:text-base">Advisory Points</h4>
                                <ul className="space-y-1 text-slate-600 max-sm:text-sm">
                                  {analysis.recommendations.advisoryPoints.map((point: string, i: number) => (
                                    <li key={i} className="flex items-start">
                                      <span className="text-brand-blue mr-2">•</span>
                                      {point}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                  </DebugSection>
                </div>

                {/* Chat Panel - Option 3: Use effectiveStats for selected month */}
                <div className="col-span-1 max-lg:col-span-1">
                  <AIChatHistory
                    context={{
                      verascore: Number(effectiveStats?.verascore ?? 0),
                      factors: FACTORS.map((f: any) => {
                        const value = effectiveStats ? Number((effectiveStats as any)[f.key]) : 0;
                        return {
                          key: String(f.key),
                          score: value,
                          health: getHealthStatus(value),
                        };
                      }),
                      memberStats: effectiveStats ?? ({} as FinancialHealthStats),
                    }}
                  />
                </div>
              </div>
            </DebugSection>
          </div>
        ) : activeTab === 'connect' ? (
          /* --- Connect Tab --- (unchanged UI blocks) */
          <div className="p-6 rounded-lg bg-white/95 shadow-lg relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-brand-blue/5 to-brand-purple/5 backdrop-blur-sm" />
            <div className="relative z-10">
              <DraggableConnectSections
                sections={[
                  {
                    id: 'member-profile',
                    title: 'Member Profile',
                    content: (
                      <div className="relative">
                        <div className="flex flex-col items-center mb-5 pb-4 border-b border-slate-100 relative">
                          {/* Avatar — Demo2: prefer persona photo, fallback to initials */}
                          {editedMember.photo ? (
                            <img
                              src={editedMember.photo}
                              alt={`${editedMember.first_name} ${editedMember.last_name}`}
                              className="w-24 h-24 rounded-full object-cover shadow-md"
                              onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                            />
                          ) : (
                            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-brand-blue to-brand-purple flex items-center justify-center text-white text-2xl font-bold shadow-md">
                              {getMemberInitials(editedMember)}
                            </div>
                          )}
                          
                          {/* Name - shown once under avatar */}
                          <h4 className="text-slate-800 font-semibold text-lg mt-3">
                            {editedMember.first_name} {editedMember.last_name}
                          </h4>
                          
                          {/* Email and Phone on same line */}
                          <div className="flex items-center gap-3 mt-2 text-sm text-slate-600">
                            <span>{editedMember.email || 'No email'}</span>
                            <span className="text-slate-400">|</span>
                            <span>{getPhoneNumber(editedMember.phone_number, editedMember.uuid).display}</span>
                          </div>
                        </div>

                        <div className="space-y-3 mb-5 pb-4 border-b border-slate-100">
                          {/* Name Field */}
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <p className="text-xs font-medium text-slate-500 mb-1">Full Name</p>
                                {editingField === 'name' ? (
                                  <div className="flex gap-2">
                                    <input
                                      type="text"
                                      value={editedMember.first_name || ''}
                                      onChange={(e) => setEditedMember({ ...editedMember, first_name: e.target.value })}
                                      className="flex-1 px-2 py-1 text-sm border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-brand-blue"
                                      placeholder="First Name"
                                    />
                                    <input
                                      type="text"
                                      value={editedMember.last_name || ''}
                                      onChange={(e) => setEditedMember({ ...editedMember, last_name: e.target.value })}
                                      className="flex-1 px-2 py-1 text-sm border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-brand-blue"
                                      placeholder="Last Name"
                                    />
                                  </div>
                                ) : (
                                  <p className="text-slate-700 text-sm">{editedMember.first_name} {editedMember.last_name}</p>
                                )}
                              </div>
                              {editingField !== 'name' && (
                                <button 
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setEditingField('name');
                                  }}
                                  className="relative z-20 text-slate-400 hover:text-brand-blue transition-all p-1 ml-2 flex-shrink-0 pointer-events-auto"
                                  title="Edit name"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                  </svg>
                                </button>
                              )}
                            </div>
                            {editingField === 'name' && (
                              <div className="flex justify-end gap-2">
                                <button
                                  onClick={() => {
                                    setEditedMember(member);
                                    setEditingField(null);
                                  }}
                                  className="px-3 py-1 text-xs font-medium text-slate-600 hover:text-slate-800 transition-colors"
                                >
                                  Cancel
                                </button>
                                <button
                                  onClick={async () => {
                                    setIsSavingField(true);
                                    try {
                                      const updates = { 
                                        first_name: editedMember.first_name, 
                                        last_name: editedMember.last_name,
                                        member_name: `${editedMember.first_name} ${editedMember.last_name}`
                                      };
                                      await updateMember(editedMember.uuid, updates);
                                      setEditingField(null);
                                      setTimeout(() => window.location.reload(), 500);
                                    } catch (error) {
                                      console.error('Error saving:', error);
                                      alert('Failed to save. Please try again.');
                                    } finally {
                                      setIsSavingField(false);
                                    }
                                  }}
                                  disabled={isSavingField}
                                  className="px-3 py-1 text-xs font-medium text-white rounded transition-all disabled:opacity-50"
                                  style={{ background: 'linear-gradient(to bottom, #6b7280, #4b5563)' }}
                                >
                                  {isSavingField ? 'Saving...' : 'Save'}
                                </button>
                              </div>
                            )}
                          </div>

                          {/* Email Field */}
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <p className="text-xs font-medium text-slate-500 mb-1">Email</p>
                                {editingField === 'email' ? (
                                  <input
                                    type="email"
                                    value={editedMember.email || ''}
                                    onChange={(e) => setEditedMember({ ...editedMember, email: e.target.value })}
                                    className="w-full px-2 py-1 text-sm border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-brand-blue"
                                  />
                                ) : (
                                  <p className="text-slate-700 text-sm break-words">{editedMember.email}</p>
                                )}
                              </div>
                              {editingField !== 'email' && (
                                <button 
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setEditingField('email');
                                  }}
                                  className="relative z-20 text-slate-400 hover:text-brand-blue transition-all p-1 ml-2 flex-shrink-0 pointer-events-auto"
                                  title="Edit email"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                  </svg>
                                </button>
                              )}
                            </div>
                            {editingField === 'email' && (
                              <div className="flex justify-end gap-2">
                                <button onClick={() => { setEditedMember(member); setEditingField(null); }} className="px-3 py-1 text-xs font-medium text-slate-600 hover:text-slate-800 transition-colors">Cancel</button>
                                <button onClick={async () => { setIsSavingField(true); try { await updateMember(editedMember.uuid, { email: editedMember.email }); setEditingField(null); setTimeout(() => window.location.reload(), 500); } catch (error) { console.error('Error saving:', error); alert('Failed to save. Please try again.'); } finally { setIsSavingField(false); } }} disabled={isSavingField} className="px-3 py-1 text-xs font-medium text-white rounded transition-all disabled:opacity-50" style={{ background: 'linear-gradient(to bottom, #6b7280, #4b5563)' }}>{isSavingField ? 'Saving...' : 'Save'}</button>
                              </div>
                            )}
                          </div>

                          {/* Phone Field */}
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <p className="text-xs font-medium text-slate-500 mb-1">Phone</p>
                                {editingField === 'phone' ? (
                                  <input
                                    type="tel"
                                    value={editedMember.phone_number || ''}
                                    onChange={(e) => setEditedMember({ ...editedMember, phone_number: e.target.value })}
                                    className="w-full px-2 py-1 text-sm border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-brand-blue"
                                  />
                                ) : (
                                  <p className="text-slate-700 text-sm">{getPhoneNumber(editedMember.phone_number, editedMember.uuid).display}</p>
                                )}
                              </div>
                              {editingField !== 'phone' && (
                                <button 
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setEditingField('phone');
                                  }}
                                  className="relative z-20 text-slate-400 hover:text-brand-blue transition-all p-1 ml-2 flex-shrink-0 pointer-events-auto"
                                  title="Edit phone"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                  </svg>
                                </button>
                              )}
                            </div>
                            {editingField === 'phone' && (
                              <div className="flex justify-end gap-2">
                                <button onClick={() => { setEditedMember(member); setEditingField(null); }} className="px-3 py-1 text-xs font-medium text-slate-600 hover:text-slate-800 transition-colors">Cancel</button>
                                <button onClick={async () => { setIsSavingField(true); try { await updateMember(editedMember.uuid, { phone_number: editedMember.phone_number }); setEditingField(null); setTimeout(() => window.location.reload(), 500); } catch (error) { console.error('Error saving:', error); alert('Failed to save. Please try again.'); } finally { setIsSavingField(false); } }} disabled={isSavingField} className="px-3 py-1 text-xs font-medium text-white rounded transition-all disabled:opacity-50" style={{ background: 'linear-gradient(to bottom, #6b7280, #4b5563)' }}>{isSavingField ? 'Saving...' : 'Save'}</button>
                              </div>
                            )}
                          </div>

                          {/* Date of Birth Field */}
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <p className="text-xs font-medium text-slate-500 mb-1">Date of Birth</p>
                                {editingField === 'dob' ? (
                                  <input
                                    type="date"
                                    value={editedMember.date_of_birth || ''}
                                    onChange={(e) => setEditedMember({ ...editedMember, date_of_birth: e.target.value })}
                                    className="w-full px-2 py-1 text-sm border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-brand-blue"
                                  />
                                ) : (
                                  <p className="text-slate-700 text-sm">{editedMember.date_of_birth}</p>
                                )}
                              </div>
                              {editingField !== 'dob' && (
                                <button 
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setEditingField('dob');
                                  }}
                                  className="relative z-20 text-slate-400 hover:text-brand-blue transition-all p-1 ml-2 flex-shrink-0 pointer-events-auto"
                                  title="Edit date of birth"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                  </svg>
                                </button>
                              )}
                            </div>
                            {editingField === 'dob' && (
                              <div className="flex justify-end gap-2">
                                <button onClick={() => { setEditedMember(member); setEditingField(null); }} className="px-3 py-1 text-xs font-medium text-slate-600 hover:text-slate-800 transition-colors">Cancel</button>
                                <button onClick={async () => { setIsSavingField(true); try { await updateMember(editedMember.uuid, { date_of_birth: editedMember.date_of_birth }); setEditingField(null); setTimeout(() => window.location.reload(), 500); } catch (error) { console.error('Error saving:', error); alert('Failed to save. Please try again.'); } finally { setIsSavingField(false); } }} disabled={isSavingField} className="px-3 py-1 text-xs font-medium text-white rounded transition-all disabled:opacity-50" style={{ background: 'linear-gradient(to bottom, #6b7280, #4b5563)' }}>{isSavingField ? 'Saving...' : 'Save'}</button>
                              </div>
                            )}
                          </div>

                          {/* Gender Field */}
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <p className="text-xs font-medium text-slate-500 mb-1">Gender</p>
                                {editingField === 'gender' ? (
                                  <select
                                    value={editedMember.gender || 'N/A'}
                                    onChange={(e) => setEditedMember({ ...editedMember, gender: e.target.value })}
                                    className="w-full px-2 py-1 text-sm border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-brand-blue"
                                  >
                                    <option value="N/A">N/A</option>
                                    <option value="Male">Male</option>
                                    <option value="Female">Female</option>
                                    <option value="Other">Other</option>
                                  </select>
                                ) : (
                                  <p className="text-slate-700 text-sm">{editedMember.gender || 'N/A'}</p>
                                )}
                              </div>
                              {editingField !== 'gender' && (
                                <button 
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setEditingField('gender');
                                  }}
                                  className="relative z-20 text-slate-400 hover:text-brand-blue transition-all p-1 ml-2 flex-shrink-0 pointer-events-auto"
                                  title="Edit gender"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                  </svg>
                                </button>
                              )}
                            </div>
                            {editingField === 'gender' && (
                              <div className="flex justify-end gap-2">
                                <button onClick={() => { setEditedMember(member); setEditingField(null); }} className="px-3 py-1 text-xs font-medium text-slate-600 hover:text-slate-800 transition-colors">Cancel</button>
                                <button onClick={async () => { setIsSavingField(true); try { await updateMember(editedMember.uuid, { gender: editedMember.gender }); setEditingField(null); setTimeout(() => window.location.reload(), 500); } catch (error) { console.error('Error saving:', error); alert('Failed to save. Please try again.'); } finally { setIsSavingField(false); } }} disabled={isSavingField} className="px-3 py-1 text-xs font-medium text-white rounded transition-all disabled:opacity-50" style={{ background: 'linear-gradient(to bottom, #6b7280, #4b5563)' }}>{isSavingField ? 'Saving...' : 'Save'}</button>
                              </div>
                            )}
                          </div>
                        </div>

                        <h5 className="text-sm font-medium text-slate-700 mb-3">Address Information</h5>
                        <div className="space-y-3">
                          {/* Street Address Field */}
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <p className="text-xs font-medium text-slate-500 mb-1">Street Address</p>
                                {editingField === 'street' ? (
                                  <input
                                    type="text"
                                    value={editedMember.street_address || ''}
                                    onChange={(e) => setEditedMember({ ...editedMember, street_address: e.target.value })}
                                    className="w-full px-2 py-1 text-sm border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-brand-blue"
                                  />
                                ) : (
                                  <p className="text-slate-700 text-sm">{editedMember.street_address}</p>
                                )}
                              </div>
                              {editingField !== 'street' && (
                                <button 
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setEditingField('street');
                                  }}
                                  className="relative z-20 text-slate-400 hover:text-brand-blue transition-all p-1 ml-2 flex-shrink-0 pointer-events-auto"
                                  title="Edit street address"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                  </svg>
                                </button>
                              )}
                            </div>
                            {editingField === 'street' && (
                              <div className="flex justify-end gap-2">
                                <button onClick={() => { setEditedMember(member); setEditingField(null); }} className="px-3 py-1 text-xs font-medium text-slate-600 hover:text-slate-800 transition-colors">Cancel</button>
                                <button onClick={async () => { setIsSavingField(true); try { await updateMember(editedMember.uuid, { street_address: editedMember.street_address }); setEditingField(null); setTimeout(() => window.location.reload(), 500); } catch (error) { console.error('Error saving:', error); alert('Failed to save. Please try again.'); } finally { setIsSavingField(false); } }} disabled={isSavingField} className="px-3 py-1 text-xs font-medium text-white rounded transition-all disabled:opacity-50" style={{ background: 'linear-gradient(to bottom, #6b7280, #4b5563)' }}>{isSavingField ? 'Saving...' : 'Save'}</button>
                              </div>
                            )}
                          </div>

                          {/* City Field */}
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <p className="text-xs font-medium text-slate-500 mb-1">City</p>
                                {editingField === 'city' ? (
                                  <input
                                    type="text"
                                    value={editedMember.city || ''}
                                    onChange={(e) => setEditedMember({ ...editedMember, city: e.target.value })}
                                    className="w-full px-2 py-1 text-sm border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-brand-blue"
                                  />
                                ) : (
                                  <p className="text-slate-700 text-sm">{editedMember.city}</p>
                                )}
                              </div>
                              {editingField !== 'city' && (
                                <button 
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setEditingField('city');
                                  }}
                                  className="relative z-20 text-slate-400 hover:text-brand-blue transition-all p-1 ml-2 flex-shrink-0 pointer-events-auto"
                                  title="Edit city"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                  </svg>
                                </button>
                              )}
                            </div>
                            {editingField === 'city' && (
                              <div className="flex justify-end gap-2">
                                <button onClick={() => { setEditedMember(member); setEditingField(null); }} className="px-3 py-1 text-xs font-medium text-slate-600 hover:text-slate-800 transition-colors">Cancel</button>
                                <button onClick={async () => { setIsSavingField(true); try { await updateMember(editedMember.uuid, { city: editedMember.city }); setEditingField(null); setTimeout(() => window.location.reload(), 500); } catch (error) { console.error('Error saving:', error); alert('Failed to save. Please try again.'); } finally { setIsSavingField(false); } }} disabled={isSavingField} className="px-3 py-1 text-xs font-medium text-white rounded transition-all disabled:opacity-50" style={{ background: 'linear-gradient(to bottom, #6b7280, #4b5563)' }}>{isSavingField ? 'Saving...' : 'Save'}</button>
                              </div>
                            )}
                          </div>

                          {/* State Field */}
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <p className="text-xs font-medium text-slate-500 mb-1">State</p>
                                {editingField === 'state' ? (
                                  <select
                                    value={editedMember.state || ''}
                                    onChange={(e) => setEditedMember({ ...editedMember, state: e.target.value })}
                                    className="w-full px-2 py-1 text-sm border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-brand-blue"
                                  >
                                    <option value="">Select state</option>
                                    {US_STATES.map(s => (
                                      <option key={s.value} value={s.value}>{s.label}</option>
                                    ))}
                                  </select>
                                ) : (
                                  <p className="text-slate-700 text-sm">{editedMember.state || '—'}</p>
                                )}
                              </div>
                              {editingField !== 'state' && (
                                <button 
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setEditingField('state');
                                  }}
                                  className="relative z-20 text-slate-400 hover:text-brand-blue transition-all p-1 ml-2 flex-shrink-0 pointer-events-auto"
                                  title="Edit state"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                  </svg>
                                </button>
                              )}
                            </div>
                            {editingField === 'state' && (
                              <div className="flex justify-end gap-2">
                                <button onClick={() => { setEditedMember(member); setEditingField(null); }} className="px-3 py-1 text-xs font-medium text-slate-600 hover:text-slate-800 transition-colors">Cancel</button>
                                <button onClick={async () => { setIsSavingField(true); try { await updateMember(editedMember.uuid, { state: editedMember.state }); setEditingField(null); setTimeout(() => window.location.reload(), 500); } catch (error) { console.error('Error saving:', error); alert('Failed to save. Please try again.'); } finally { setIsSavingField(false); } }} disabled={isSavingField} className="px-3 py-1 text-xs font-medium text-white rounded transition-all disabled:opacity-50" style={{ background: 'linear-gradient(to bottom, #6b7280, #4b5563)' }}>{isSavingField ? 'Saving...' : 'Save'}</button>
                              </div>
                            )}
                          </div>

                          {/* Zipcode Field */}
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <p className="text-xs font-medium text-slate-500 mb-1">Zipcode</p>
                                {editingField === 'zipcode' ? (
                                  <input
                                    type="text"
                                    value={editedMember.zipcode || ''}
                                    onChange={(e) => setEditedMember({ ...editedMember, zipcode: e.target.value })}
                                    className="w-full px-2 py-1 text-sm border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-brand-blue"
                                  />
                                ) : (
                                  <p className="text-slate-700 text-sm">{formatZipcode(editedMember.zipcode) || '—'}</p>
                                )}
                              </div>
                              {editingField !== 'zipcode' && (
                                <button 
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setEditingField('zipcode');
                                  }}
                                  className="relative z-20 text-slate-400 hover:text-brand-blue transition-all p-1 ml-2 flex-shrink-0 pointer-events-auto"
                                  title="Edit zipcode"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                  </svg>
                                </button>
                              )}
                            </div>
                            {editingField === 'zipcode' && (
                              <div className="flex justify-end gap-2">
                                <button onClick={() => { setEditedMember(member); setEditingField(null); }} className="px-3 py-1 text-xs font-medium text-slate-600 hover:text-slate-800 transition-colors">Cancel</button>
                                <button onClick={async () => { setIsSavingField(true); try { await updateMember(editedMember.uuid, { zipcode: editedMember.zipcode }); setEditingField(null); setTimeout(() => window.location.reload(), 500); } catch (error) { console.error('Error saving:', error); alert('Failed to save. Please try again.'); } finally { setIsSavingField(false); } }} disabled={isSavingField} className="px-3 py-1 text-xs font-medium text-white rounded transition-all disabled:opacity-50" style={{ background: 'linear-gradient(to bottom, #6b7280, #4b5563)' }}>{isSavingField ? 'Saving...' : 'Save'}</button>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Membership — Demo2: read-only persona metadata */}
                        <h5 className="text-sm font-medium text-slate-700 mt-5 mb-3">Membership</h5>
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <p className="text-xs font-medium text-slate-500">Member Since</p>
                            <p className="text-slate-700 text-sm">{editedMember.inst_member_since || '—'}</p>
                          </div>
                          <div className="flex items-center justify-between">
                            <p className="text-xs font-medium text-slate-500">Loan Advisor</p>
                            <p className="text-slate-700 text-sm">{editedMember.inst_loan_advisor || '—'}</p>
                          </div>
                          <div className="flex items-center justify-between">
                            <p className="text-xs font-medium text-slate-500">Age</p>
                            <p className="text-slate-700 text-sm">{editedMember.age && editedMember.age > 0 ? editedMember.age : '—'}</p>
                          </div>
                          <div className="flex items-center justify-between">
                            <p className="text-xs font-medium text-slate-500">Status</p>
                            <p className="text-slate-700 text-sm">{editedMember.dashboard_status || '—'}</p>
                          </div>
                        </div>
                      </div>
                    ),
                    isActionSection: false,
                  },
                  {
                    id: 'financial-insights',
                    title: 'Financial Insights',
                    content: (
                      <div>
                        <div className="flex items-center justify-between mb-5 pb-4 border-b border-slate-100">
                          <div>
                            {/* VS-BUG-001: Renamed from "Yearly Income" */}
                            <div className="mb-1">
                              <p className="text-xs font-medium text-slate-500">Annual Deposited Income</p>
                              {/* BUG-UI-062: Removed income basis badge - info is available in the modal */}
                            </div>
                            {/* BUG-UI-062 FIX: Display DEPOSITED income (verifiable), not attested */}
                            {/* Option 3: Use effectiveStats for selected month */}
                            <p className="text-slate-800 text-xl font-semibold">
                              {formatCurrency(
                                member.deposited_income ??
                                effectiveStats?.income_basis?.income_sources?.deposited_net ??
                                member.yearly_gross_income ?? 0
                              )}
                            </p>
                          </div>
                          <div className="bg-brand-blue/10 px-3 py-2 rounded-lg">
                            <p className="text-xs font-medium text-brand-blue mb-1">Member Since</p>
                            <p className="text-slate-700 text-sm font-medium">{member.inst_member_since}</p>
                          </div>
                        </div>

                        {stats.length > 0 && effectiveStats && (
                          <>
                            <div className="mb-5">
                              <div className="flex justify-between items-center mb-2">
                                <p className="text-sm font-medium text-slate-700">VeraScore</p>
                                <p className="text-brand-blue font-semibold">
                                  {Math.round(effectiveStats.verascore || 0)}
                                </p>
                              </div>
                              <div className="w-full bg-slate-200 rounded-full h-2.5">
                                <div
                                  className="bg-gradient-to-r from-brand-blue to-brand-purple h-2.5 rounded-l-full"
                                  style={{ width: `${Math.min(100, Math.max(0, effectiveStats.verascore || 0))}%` }}
                                />
                              </div>
                              <p className="text-xs text-slate-500 mt-1">Overall financial health score</p>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                              <ClickableMetric uiFieldPath="lender.current_finances.dti_ratio" runId={member.run_id || ''} memberId={member.uuid} memberName={member.member_name} initialMonth={effectiveMonth}>
                                <div className="bg-slate-50 p-3 rounded-lg hover:bg-slate-100 transition-colors">
                                  <p className="text-xs font-medium text-slate-500 mb-1">DTI Ratio</p>
                                  <p className="text-slate-800 text-sm font-semibold">
                                    {/* BUG-DATA-001 FIX: Prefer member.dti_ratio_value from lender_member_summary view */}
                                    {(member.dti_ratio_value ?? effectiveStats.dti_ratio ?? 0).toFixed(1)}%
                                  </p>
                                  <p className="text-xs text-slate-500 mt-1">Debt to income ratio</p>
                                </div>
                              </ClickableMetric>
                              <ClickableMetric uiFieldPath="lender.current_finances.net_savings" runId={member.run_id || ''} memberId={member.uuid} memberName={member.member_name} initialMonth={effectiveMonth}>
                                <div className="bg-slate-50 p-3 rounded-lg hover:bg-slate-100 transition-colors">
                                  <p className="text-xs font-medium text-slate-500 mb-1">Net Savings</p>
                                  <p className="text-slate-800 text-sm font-semibold">
                                    ${(effectiveStats.net_savings || 0).toLocaleString()}
                                  </p>
                                  <p className="text-xs text-slate-500 mt-1">Monthly savings</p>
                                </div>
                              </ClickableMetric>
                              <ClickableMetric uiFieldPath="lender.current_finances.money_leftover" runId={member.run_id || ''} memberId={member.uuid} memberName={member.member_name} initialMonth={effectiveMonth}>
                                <div className="bg-slate-50 p-3 rounded-lg hover:bg-slate-100 transition-colors">
                                  <p className="text-xs font-medium text-slate-500 mb-1">Money Leftover</p>
                                  <p className="text-slate-800 text-sm font-semibold">
                                    ${(effectiveStats.money_leftover || 0).toLocaleString()}
                                  </p>
                                  <p className="text-xs text-slate-500 mt-1">After expenses</p>
                                </div>
                              </ClickableMetric>
                              <ClickableMetric uiFieldPath="lender.current_finances.monthly_expenses" runId={member.run_id || ''} memberId={member.uuid} memberName={member.member_name} initialMonth={effectiveMonth}>
                                <div className="bg-slate-50 p-3 rounded-lg hover:bg-slate-100 transition-colors">
                                  <p className="text-xs font-medium text-slate-500 mb-1">Monthly Expenses</p>
                                  <p className="text-slate-800 text-sm font-semibold">
                                    ${(effectiveStats.monthly_expenses || 0).toLocaleString()}
                                  </p>
                                  <p className="text-xs text-slate-500 mt-1">Total spending</p>
                                </div>
                              </ClickableMetric>
                            </div>
                          </>
                        )}
                      </div>
                    ),
                    isActionSection: false,
                  },
                  {
                    id: 'personalized-recommendations',
                    title: 'Personalized Recommendations',
                    content: <ConnectCardsDndKitInline />,
                    isActionSection: true,
                  },
                  {
                    id: 'advisor-notes',
                    title: 'Advisor Notes',
                    content: <AdvisorNotesSection onAssist={onAssist} onEmail={onEmail} member={member} />,
                    isActionSection: true,
                  },
                  {
                    id: 'recent-touchpoints',
                    title: 'Recent Touchpoints',
                    content: (
                      <div className="space-y-4">
                        {/* ... kept same content ... */}
                        <div className="bg-slate-50 p-3 rounded-lg border-l-4 border-brand-blue">
                          <div className="flex justify-between items-start">
                            <p className="text-slate-800 text-sm font-medium">Email Campaign</p>
                            <span className="text-xs text-slate-500">
                              {format(new Date(Date.now() - 3 * 24 * 60 * 60 * 1000), 'MMM d')}
                            </span>
                          </div>
                          <p className="text-slate-600 text-xs mt-1">Sent quarterly newsletter with financial tips</p>
                          <div className="flex justify-between items-center mt-2">
                            <span className="text-xs text-brand-blue">Opened: Yes</span>
                            <span className="text-xs text-slate-500">Advisor: {member.inst_loan_advisor}</span>
                          </div>
                        </div>

                        <div className="bg-slate-50 p-3 rounded-lg border-l-4 border-brand-purple">
                          <div className="flex justify-between items-start">
                            <p className="text-slate-800 text-sm font-medium">Phone Call</p>
                            <span className="text-xs text-slate-500">
                              {format(new Date(Date.now() - 10 * 24 * 60 * 60 * 1000), 'MMM d')}
                            </span>
                          </div>
                          <p className="text-slate-600 text-xs mt-1">Discussed loan refinancing options</p>
                          <div className="flex justify-between items-center mt-2">
                            <span className="text-xs text-brand-purple">Duration: 15 min</span>
                            <span className="text-xs text-slate-500">Advisor: {member.inst_loan_advisor}</span>
                          </div>
                        </div>

                        <div className="bg-slate-50 p-3 rounded-lg border-l-4 border-brand-blue">
                          <div className="flex justify-between items-start">
                            <p className="text-slate-800 text-sm font-medium">In-Branch Visit</p>
                            <span className="text-xs text-slate-500">
                              {format(new Date(Date.now() - 25 * 24 * 60 * 60 * 1000), 'MMM d')}
                            </span>
                          </div>
                          <p className="text-slate-600 text-xs mt-1">Updated personal information and discussed savings goals</p>
                          <div className="flex justify-between items-center mt-2">
                            <span className="text-xs text-brand-blue">Branch: Main St.</span>
                            <span className="text-xs text-slate-500">Advisor: {member.inst_loan_advisor}</span>
                          </div>
                        </div>
                      </div>
                    ),
                    isActionSection: false,
                  },
                  {
                    id: 'compliance-log',
                    title: 'Compliance Log',
                    content: <ComplianceLogSection />,
                    isActionSection: false,
                  },
                  {
                    id: 'advisor-impact',
                    title: 'Advisor Impact',
                    content: (
                      <div>
                        {/* ... kept same content ... */}
                        <div className="mb-5 pb-4 border-b border-slate-100">
                          <div className="flex items-center mb-3">
                            <div className="w-10 h-10 rounded-full bg-brand-blue/10 flex items-center justify-center text-brand-blue mr-3">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                              </svg>
                            </div>
                            <div>
                              <p className="text-slate-800 font-medium">Assigned Loan Advisor</p>
                              <p className="text-brand-blue font-semibold">{member.inst_loan_advisor}</p>
                            </div>
                          </div>

                          <div className="bg-green-50 p-3 rounded-lg border border-green-100 mb-3">
                            <div className="flex items-start">
                              <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-green-600 mr-3 mt-1">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                              </div>
                              <div>
                                <p className="text-green-800 font-medium text-sm">Financial Health Improvement</p>
                                <p className="text-green-600 text-xs mt-1">VeraScore increased by 12 points since first meeting</p>
                              </div>
                            </div>
                          </div>

                          <div className="bg-blue-50 p-3 rounded-lg border border-blue-100">
                            <div className="flex items-start">
                              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 mr-3 mt-1">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                  <path d="M2 10a8 8 0 018-8v8h8a8 8 0 11-16 0z" />
                                  <path d="M12 2.252A8.014 8.014 0 0117.748 8H12V2.252z" />
                                </svg>
                              </div>
                              <div>
                                <p className="text-blue-800 font-medium text-sm">Debt Reduction Progress</p>
                                <p className="text-blue-600 text-xs mt-1">DTI ratio reduced by 5.2% through advisor guidance</p>
                              </div>
                            </div>
                          </div>
                        </div>

                        <h4 className="text-slate-700 text-sm font-semibold mb-3">Common Topics</h4>
                        <div className="flex flex-wrap gap-2 mb-5">
                          <span className="bg-brand-blue/10 text-brand-blue text-xs px-2 py-1 rounded-full">Savings</span>
                          <span className="bg-brand-purple/10 text-brand-purple text-xs px-2 py-1 rounded-full">Loan Options</span>
                          <span className="bg-green-100 text-green-700 text-xs px-2 py-1 rounded-full">Retirement</span>
                          <span className="bg-yellow-100 text-yellow-700 text-xs px-2 py-1 rounded-full">Debt Management</span>
                          <span className="bg-red-100 text-red-700 text-xs px-2 py-1 rounded-full">Credit Score</span>
                        </div>
                      </div>
                    ),
                    isActionSection: false,
                  },
                ]}
              />
            </div>
          </div>
        ) : null}

        {/* Score Details */}
        {isExpanded && (
          <>
            {isLoadingHistory ? (
              <div className="flex items-center justify-center py-12">
                <LoadingSpinner />
                <span className="ml-3 text-slate-600">Loading detailed history...</span>
              </div>
            ) : historyError ? (
              <div className="py-6">
                <ErrorMessage message={`Failed to load score details: ${historyError}`} />
              </div>
            ) : (
              <>
            <DebugSection label="Score Details Heading" className="mt-6 mb-2">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <h2 className="text-xl font-semibold text-slate-800">
                  {/* BUG-UI-096 FIX: Use formatMonthSafe to avoid timezone issues */}
                  Score and Factors for: {effectiveMonth
                    ? formatMonthSafe(effectiveMonth)
                    : getCurrentMonthFormatted(stats, 'Current Period')}
                </h2>

                {/*
                  Per-card month selector - DISABLED (January 24, 2026)

                  BUSINESS DECISION: Disable per-card month dropdown for the same reasons as the
                  global month dropdown:

                  1. DEFAULT TO MOST RECENT DATA: The dashboard should always show the most recent
                     month with complete financial data. Users don't need to manually select months.

                  2. MODAL MONTH SELECTION: Historical month viewing is available in the Evidence
                     Modal via its month dropdown, which is the appropriate place for drill-down.

                  3. REDUCED UI COMPLEXITY: Fewer controls = cleaner UX. The per-card dropdown
                     was confusing users who didn't understand the difference between global and
                     card-specific month selection.

                  4. DATA INTEGRITY: By defaulting to the most recent complete data month, we ensure
                     lenders always see fully processed and verified financial information.

                  The cardSelectedMonth state and effectiveMonth logic remain in place for potential
                  future re-enablement. To re-enable: Uncomment the JSX block below.

                  See: Audit Framework Documentation section 19.7 for full rationale.
                */}
                {/* COMMENTED OUT - Per-card month selector
                <div className="flex items-center gap-2">
                  {cardSelectedMonth && cardSelectedMonth !== globalMonth && (
                    <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full">
                      Custom month selected
                    </span>
                  )}

                  <select
                    value={cardSelectedMonth || ''}
                    onChange={(e) => setCardSelectedMonth(e.target.value || null)}
                    className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 bg-white hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Use Global Month</option>
                    {stats
                      .map(s => s.month)
                      .filter((m, i, arr) => arr.indexOf(m) === i)
                      .sort((a, b) => b.localeCompare(a))
                      .map(month => (
                        <option key={month} value={month}>
                          {formatMonthSafe(month)}
                          {month === globalMonth ? ' (Global)' : ''}
                        </option>
                      ))}
                  </select>
                </div>
                */}
              </div>
            </DebugSection>

            {/* Credit Journey Timeline - US 7.35 */}
            <DebugSection label="Credit Journey Timeline" className="mt-6 mb-6">
              <CreditJourneyTimeline events={buildTimelineEvents(member, detailedHistory)} />
            </DebugSection>

            {/* Audit Report Panel - Epic 54 */}
            <DebugSection label="Audit Report Panel" className="mt-6 mb-6">
              <AuditReportPanel
                stats={stats}
                memberUuid={member.uuid}
                runId={monthStats?.source_snapshot_id ?? stats[0]?.source_snapshot_id ?? undefined}
                showDebug={showDebug}
                selectedMonth={effectiveMonth}
                onFactorDrilldown={(factorKey) => {
                  // Map factor keys to UI field paths for evidence modal
                  const factorToUiPath: Record<string, string> = {
                    'factor_dti': 'lender.factors.dti',
                    'factor_cash_on_hand': 'lender.factors.cash_on_hand',
                    'factor_spending': 'lender.factors.spending',
                    'factor_savings': 'lender.factors.savings',
                    'factor_debt': 'lender.factors.debt',
                    'factor_payment_history': 'lender.factors.payment_history',
                  };

                  // Open evidence modal for this factor
                  // BUG-UI-092: Pass memberId for cross-run historical evidence
                  // Option 3: Pass initialMonth so modal opens at selected month
                  const uiFieldPath = factorToUiPath[factorKey];
                  const runId = monthStats?.source_snapshot_id || stats[0]?.source_snapshot_id || member.run_id;
                  if (uiFieldPath && runId) {
                    showEvidence({
                      uiFieldPath,
                      runId,
                      memberId: member.uuid,
                      disclosureLevel: 'LENDER',
                      memberName: member.member_name,
                      initialMonth: effectiveMonth,
                    });
                  }

                  // Also activate the corresponding factor for the chart
                  const factor = FACTORS.find((f: any) => f.key === factorKey);
                  if (factor) {
                    setActiveFactor(factor as unknown as Factor);
                  }
                }}
              />
            </DebugSection>

            {/* Factor Details Copy */}
            <DebugSection label="Section 3: Factor Details Copy" className="mt-4 mb-6">
              <div className="flex flex-col gap-6">
                <div className="grid grid-cols-12 gap-6 max-lg:grid-cols-1 max-lg:gap-6">
                  {/* Section A: Factor Cards */}
                  <DebugSection label="Section A: Factor Cards" className="col-span-6 max-lg:col-span-full">
                    <div className="grid grid-cols-2 gap-4 max-sm:grid-cols-1">
                      {FACTORS.filter((f: any) => f.key === 'verascore').map((factor: any, index: number) => {
                        const typedFactor = factor as Factor;
                        const score = Math.round(getCurrentValue(stats, typedFactor.key));
                        const health = getHealthStatus(score);

                        return (
                          <DebugSection
                            key={String(typedFactor.key)}
                            label={`Section A-${index + 1}: ${typedFactor.label}`}
                            className="col-span-2 max-sm:col-span-1"
                          >
                            {isMobile ? (
                              <div className="w-full p-4 rounded-lg shadow-sm border border-slate-200 bg-white">
                                <ScoreTrigger
                                  score={score}
                                  title={typedFactor.label}
                                  status={health}
                                  chartComponent={
                                    <VeraScoreChart
                                      data={[...stats].sort((a, b) => new Date(a.month).getTime() - new Date(b.month).getTime()).slice(-12)}
                                      showDebug={false}
                                      monthsToShow={12}
                                      runId={member.run_id}
                                      memberId={member.uuid}
                                      memberName={member.member_name}
                                    />
                                  }
                                  icon={getStatusIcon(score)}
                                />
                              </div>
                            ) : (
                              <button
                                onClick={() => setActiveFactor(typedFactor)}
                                className={`w-full p-4 rounded-lg transition-colors shadow-sm border border-slate-200 ${activeFactor.key === typedFactor.key ? 'bg-brand-blue text-white border-transparent' : 'bg-white hover:bg-slate-50'}`}
                              >
                                <div className="grid grid-cols-[auto_1fr_auto] gap-4 items-end">
                                  <DebugSection label={`Section A-${index + 1}-1: Icon`}>
                                    <div className="pb-4">{getStatusIcon(score)}</div>
                                  </DebugSection>
                                  <DebugSection label={`Section A-${index + 1}-2: Score Info`}>
                                    <div className="text-left">
                                      <div className="font-bold">{typedFactor.label}</div>
                                      <div className="text-3xl font-bold mt-1 max-sm:text-2xl">{score}</div>
                                    </div>
                                  </DebugSection>
                                  <DebugSection label={`Section A-${index + 1}-3: Health`}>
                                    <div className={`text-xs self-end text-right ${activeFactor.key === typedFactor.key ? 'text-white' : 'text-slate-600'}`}>{health}</div>
                                  </DebugSection>
                                </div>
                              </button>
                            )}
                          </DebugSection>
                        );
                      })}

                      {FACTORS.filter((f: any) => f.key !== 'verascore').map((factor: any, index: number) => {
                        const typedFactor = factor as Factor;
                        const score = getCurrentValue(stats, typedFactor.key);
                        const health = getHealthStatus(score);
                        const dtiRatio = typedFactor.key === 'factor_dti' ? getCurrentValue(stats, 'dti_ratio') : null;
                        // BUG-UI-127 FIX: Get cash_lr_ratio with fallback calculation
                        // cash_lr_ratio = liquid_reserves / (monthly_expenses + minimum_debt_payments)
                        // This represents months of expenses covered by liquid reserves
                        let cashLRRatio: number | null = null;
                        if (typedFactor.key === 'factor_cash_on_hand') {
                          const storedRatio = getCurrentValue(stats, 'cash_lr_ratio');
                          if (storedRatio !== null && storedRatio !== undefined && storedRatio > 0) {
                            cashLRRatio = storedRatio;
                          } else {
                            // Fallback: calculate from liquid_reserves, monthly_expenses, and minimum_debt_payments
                            const liquidReserves = getCurrentValue(stats, 'liquid_reserves');
                            const monthlyExpenses = getCurrentValue(stats, 'monthly_expenses');
                            const minimumDebtPayments = getCurrentValue(stats, 'minimum_debt_payments');
                            const totalMonthlyOutflow = (monthlyExpenses || 0) + (minimumDebtPayments || 0);
                            if (totalMonthlyOutflow > 0 && liquidReserves !== null && liquidReserves !== undefined) {
                              cashLRRatio = liquidReserves / totalMonthlyOutflow;
                            }
                          }
                        }
                        // US 7.36: Calculate 6-month average for trend arrow
                        const sixMonthAvg = getAverageValue(stats, typedFactor.key as keyof FinancialHealthStats, 6);

                        // BUG-UI-130 FIX: Use spending_cf_ratio for "% left over" display
                        // spending_cf_ratio = (money_leftover / gross_income) * 100, which represents
                        // the percentage of income left after expenses (i.e., actual "% left over")
                        let leftoverPercentage: number | null = null;
                        if (typedFactor.key === 'factor_spending') {
                          // First try to get spending_cf_ratio from stats (the actual cash flow ratio)
                          const spendingCfRatio = getCurrentValue(stats, 'spending_cf_ratio');
                          if (spendingCfRatio !== null && spendingCfRatio !== undefined) {
                            leftoverPercentage = spendingCfRatio;
                          } else {
                            // Fallback: calculate from money_leftover and gross_income
                            const currentLeftover = getCurrentValue(stats, 'money_leftover');
                            const grossIncome = getCurrentValue(stats, 'gross_income');
                            if (grossIncome > 0) {
                              leftoverPercentage = (currentLeftover / grossIncome) * 100;
                            } else {
                              leftoverPercentage = 0;
                            }
                          }
                        }

                        let savingsPercentage: number | null = null;
                        if (typedFactor.key === 'factor_savings') {
                          const currentSavings = getCurrentValue(stats, 'net_savings');
                          const averageSavings = getAverageValue(stats, 'net_savings', 24);
                          if (averageSavings > 0) savingsPercentage = ((currentSavings - averageSavings) / averageSavings) * 100;
                        }

                        const getFactorChartComponent = () => {
                          const sortedData = [...stats].sort((a, b) => new Date(a.month).getTime() - new Date(b.month).getTime());
                          const fullData = sortedData.slice(-24);
                          if (typedFactor.key === 'factor_dti') return <DebtToIncomeFactorChart data={fullData} showDebug={false} monthsToShow={12} />;
                          if (typedFactor.key === 'factor_spending') return <SpendingFactorChart data={fullData} showDebug={false} />;
                          if (typedFactor.key === 'factor_debt') return <DebtFactorChart data={fullData} showDebug={false} />;
                          if (typedFactor.key === 'factor_cash_on_hand') return <CashOnHandFactorChart data={fullData} showDebug={false} />;
                          if (typedFactor.key === 'factor_savings') return <SavingsFactorChart data={fullData} showDebug={false} />;
                          if (typedFactor.key === 'factor_payment_history') return <PaymentHistoryFactorChart data={fullData} showDebug={false} />;
                          return null;
                        };

                        const getAdditionalDescription = () => {
                          if (typedFactor.key === 'factor_dti' && dtiRatio !== null) return `DTI: ${dtiRatio.toFixed(1)}%`;
                          if (typedFactor.key === 'factor_cash_on_hand' && cashLRRatio !== null) return `${cashLRRatio.toFixed(1)} months of expenses`;
                          // BUG-UI-130 FIX: Changed from "% vs avg" to "% left over" to match actual calculation
                          if (typedFactor.key === 'factor_spending' && leftoverPercentage !== null) return `${leftoverPercentage.toFixed(0)}% left over`;
                          if (typedFactor.key === 'factor_savings' && savingsPercentage !== null) return `${savingsPercentage > 0 ? '+' : ''}${savingsPercentage.toFixed(0)}% vs avg`;
                          return '';
                        };

                        const getFactorStatus = () => {
                          if (score >= 70) return 'positive';
                          if (score >= 50) return 'neutral';
                          if (score >= 35) return 'warning';
                          return 'negative';
                        };

                        return (
                          <DebugSection
                            key={String(typedFactor.key)}
                            label={`Section A-${index + 2}: ${typedFactor.label}`}
                            className="col-span-1"
                          >
                            {isMobile ? (
                              <div className="w-full p-4 rounded-lg shadow-sm border border-slate-200 bg-white">
                                <FactorTrigger
                                  factorName={typedFactor.label}
                                  value={Math.round(score)}
                                  status={getFactorStatus()}
                                  description={getAdditionalDescription()}
                                  chartComponent={getFactorChartComponent()}
                                />
                              </div>
                            ) : (
                              <button
                                onClick={() => setActiveFactor(typedFactor)}
                                className={`w-full p-4 rounded-lg transition-colors shadow-sm border border-slate-200 ${activeFactor.key === typedFactor.key ? 'bg-brand-blue text-white border-transparent' : 'bg-white hover:bg-slate-50'}`}
                              >
                                <div className="grid grid-cols-[auto_1fr_auto] gap-4 items-end">
                                  <DebugSection label={`Section A-${index + 2}-1: Icon`}>
                                    <div className="pb-4">{getStatusIcon(score)}</div>
                                  </DebugSection>
                                  <DebugSection label={`Section A-${index + 2}-2: Score Info`}>
                                    <div className="text-left min-w-[100px]">
                                      <div className="font-bold whitespace-nowrap">{typedFactor.label}</div>
                                      <div className="text-3xl font-bold mt-1 max-sm:text-2xl flex items-center">
                                        {Math.round(score)}
                                        {/* US 7.36: Trend Arrow */}
                                        {renderSmallTrendArrow(score, sixMonthAvg)}
                                      </div>
                                      {typedFactor.key === 'factor_dti' && dtiRatio !== null && (
                                        <div className="text-xs mt-1">DTI: {(dtiRatio).toFixed(1)}%</div>
                                      )}
                                      {typedFactor.key === 'factor_cash_on_hand' && cashLRRatio !== null && (
                                        <div className="text-xs mt-1">{cashLRRatio.toFixed(1)} mo.</div>
                                      )}
                                      {typedFactor.key === 'factor_spending' && leftoverPercentage !== null && (
                                        <div className="text-xs mt-1">{leftoverPercentage.toFixed(1)}% left over</div>
                                      )}
                                      {typedFactor.key === 'factor_savings' && savingsPercentage !== null && (
                                        <div className="text-xs mt-1">{savingsPercentage.toFixed(1)}% saved</div>
                                      )}
                                    </div>
                                  </DebugSection>
                                  <DebugSection label={`Section A-${index + 2}-3: Health`}>
                                    <div className={`text-xs self-end text-right ${activeFactor.key === typedFactor.key ? 'text-white' : 'text-slate-600'}`}>{health}</div>
                                  </DebugSection>
                                </div>
                              </button>
                            )}
                          </DebugSection>
                        );
                      })}
                    </div>
                  </DebugSection>

                  {/* Section B: Factor Chart (desktop/tablet only) */}
                  {!isMobile && (
                    <DebugSection label="Section B: Factor Chart" className="col-span-6 max-lg:col-span-full">
                      <div className="bg-white p-4 rounded-lg shadow-sm border border-slate-200">
                        <DebugSection label="Section B-1: Chart Title">
                          <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-medium">{activeFactor.label} Analysis</h3>
                            <button
                              onClick={() => {
                                if (chartRef.current?.openModal) chartRef.current.openModal();
                              }}
                              className="p-1 rounded text-slate-500 hover:text-slate-700 hover:bg-slate-100 flex items-center gap-1 text-xs"
                              title="Expand chart"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="15 3 21 3 21 9"></polyline>
                                <polyline points="9 21 3 21 3 15"></polyline>
                                <line x1="21" y1="3" x2="14" y2="10"></line>
                                <line x1="3" y1="21" x2="10" y2="14"></line>
                              </svg>
                              <span>Expand</span>
                            </button>
                          </div>
                        </DebugSection>

                        <DebugSection label="Section B-2: Graph">
                          <div className="h-[510px] lg:h-[515px] xl:h-[520px] max-sm:h-[470px] pb-0">
                            <ResponsiveContainer width="100%" height="100%">
                              {renderFactorChart(activeFactor, stats, showDebug, 12, chartRef, { runId: member.run_id, memberId: member.uuid, memberName: member.member_name }) || <div>No chart data available</div>}
                            </ResponsiveContainer>
                          </div>
                        </DebugSection>
                      </div>
                    </DebugSection>
                  )}
                </div>
              </div>
            </DebugSection>

            {/* Original Score Details */}
            <DebugSection label="Section 4: Score Details" className="mt-6">
              <div className="grid grid-cols-2 gap-6 bg-white rounded-lg p-6 border border-slate-200 max-lg:grid-cols-1">
                {/* Left Column: Financial Metrics */}
                <DebugSection label="Section 4A: Financial Metrics List" className="space-y-2">
                  <DebugSection label="Section 4A-1: Current Month" className="mb-4">
                    <div className="text-lg font-semibold">
                      Current Finances and Trends
                      {/* BUG-UI-096 FIX: Use formatMonthSafe to avoid timezone issues */}
                      {effectiveMonth && (
                        <span className="text-sm font-normal text-slate-500 ml-2">
                          ({formatMonthSafe(effectiveMonth, 'MMM yyyy')})
                        </span>
                      )}
                    </div>
                  </DebugSection>

                  <DebugSection label="Section 4A-2: Metrics List" className="space-y-1">
                    {(FINANCIAL_METRICS as unknown as FinancialMetric[])
                      .filter(metric => metric.key !== 'verascore')
                      .sort((a, b) => (a.key === 'credit_score' ? 1 : b.key === 'credit_score' ? -1 : 0))
                      .map((metric: FinancialMetric, index: number) => {
                        // Option 3: Use effectiveMonth when set (global or card-specific month selection)
                        // Fall back to getLatestValueWithData only when no month is selected (shouldn't happen)
                        let metricValue: number;
                        let dataMonth: string | null = null;
                        let isFromDifferentMonth = false;

                        if (effectiveMonth && monthStats) {
                          // Use the selected month's data directly
                          if (metric.key === 'credit_score') {
                            // BUG-DATA-003 FIX: Use getCreditScoreForStat with member overrides for consistency
                            // This ensures the credit score in "Current Finances and Trends" matches the header
                            metricValue = getCreditScoreForStat(effectiveStats, {
                              number_of_late_payments: member.number_of_late_payments,
                              age_of_late_payments: member.age_of_late_payments,
                              dti_ratio_value: member.dti_ratio_value,
                              credit_score: member.credit_score
                            });
                          } else if (metric.key === 'gross_income') {
                            // BUG-UI-125 FIX: Use Plaid-derived deposited_income as primary source
                            // Priority: member.deposited_income > income_basis.deposited_net > yearly_gross_income
                            // This ensures consistency with Annual Deposited Income at the top of the card
                            const depositedFromMember = member.deposited_income;
                            const depositedFromStats = effectiveStats?.income_basis?.income_sources?.deposited_net;
                            const depositedIncome = depositedFromMember ?? depositedFromStats ?? 0;

                            if (depositedIncome > 0) {
                              // Use verified Plaid-derived income / 12
                              metricValue = depositedIncome / 12;
                            } else {
                              // Fallback to yearly_gross_income / 12 if no deposited income
                              const yearlyIncome = Number(monthStats.yearly_gross_income ?? 0);
                              metricValue = yearlyIncome > 0 ? yearlyIncome / 12 : 0;
                            }
                          } else if (metric.key === 'dti_ratio') {
                            // BUG-DATA-001 FIX: Prefer member.dti_ratio_value from lender_member_summary view
                            // This is the authoritative source from algo_run_evidence
                            const memberDti = member.dti_ratio_value;
                            if (memberDti != null && memberDti > 0) {
                              metricValue = memberDti;
                            } else {
                              // Fallback: BUG-UI-126 FIX: Calculate DTI ratio
                              // DTI = (minimum_debt_payments / gross_income) * 100
                              const directDti = Number(monthStats.dti_ratio ?? 0);
                              if (directDti === 0) {
                                const minDebtPayments = Number(monthStats.minimum_debt_payments ?? 0);
                                // BUG-UI-125: Use Plaid-derived deposited_income for consistency
                                const depositedFromMember = member.deposited_income;
                                const depositedFromStats = effectiveStats?.income_basis?.income_sources?.deposited_net;
                                const depositedIncome = depositedFromMember ?? depositedFromStats ?? 0;

                                let grossIncome = depositedIncome > 0 ? depositedIncome / 12 : 0;
                                if (grossIncome === 0) {
                                  // Fallback to yearly_gross_income / 12
                                  const yearlyIncome = Number(monthStats.yearly_gross_income ?? 0);
                                  grossIncome = yearlyIncome > 0 ? yearlyIncome / 12 : 0;
                                }
                                metricValue = grossIncome > 0 ? (minDebtPayments / grossIncome) * 100 : 0;
                              } else {
                                metricValue = directDti;
                              }
                            }
                          } else if (metric.key === 'number_of_late_payments') {
                            // BUG-DATA-002 FIX: Prefer member.number_of_late_payments from lender_member_summary view
                            // This is the authoritative source from algo_run_evidence
                            const memberLatePayments = member.number_of_late_payments;
                            if (memberLatePayments != null) {
                              metricValue = memberLatePayments;
                            } else {
                              metricValue = Number(monthStats.number_of_late_payments ?? 0);
                            }
                          } else {
                            metricValue = Number(monthStats[metric.key as keyof FinancialHealthStats] ?? 0);
                          }
                          dataMonth = monthStats.month;
                          isFromDifferentMonth = false; // Using exact selected month
                        } else {
                          // Fallback: Use latest month with data (original behavior)
                          const result = metric.key !== 'credit_score'
                            ? getLatestValueWithData(stats, metric.key as keyof FinancialHealthStats)
                            : { value: getMockCreditScore(stats, member.credit_score), month: null, isFromDifferentMonth: false };
                          metricValue = result.value;
                          dataMonth = result.month;
                          isFromDifferentMonth = result.isFromDifferentMonth;
                        }

                        const formattedValue = formatValue(metricValue, metric.format);
                        // Format the month indicator (e.g., "Nov" if data is from different month than expected)
                        const monthIndicator = isFromDifferentMonth && dataMonth
                          ? ` (${new Date(dataMonth).toLocaleDateString('en-US', { month: 'short' })})`
                          : '';

                        const getMetricChartComponent = () => {
                          const sortedData = [...stats].sort((a, b) => new Date(a.month).getTime() - new Date(b.month).getTime());
                          const filteredData = sortedData.slice(-12);
                          switch (metric.key) {
                            case 'gross_income': return <GrossIncomeChart data={filteredData} showDebug={false} />;
                            case 'minimum_debt_payments': return <MinimumDebtPaymentsChart data={filteredData} showDebug={false} />;
                            case 'monthly_expenses': return <MonthlyExpensesChart data={filteredData} showDebug={false} />;
                            case 'money_in': return <MoneyInChart data={filteredData} showDebug={false} />;
                            case 'money_leftover': return <MoneyLeftoverChart data={filteredData} showDebug={false} />;
                            case 'net_savings': return <NetSavingsChart data={filteredData} showDebug={false} />;
                            case 'credit_score': return <CreditScoreChartWrapper data={filteredData} showDebug={false} runId={member.run_id} memberId={member.uuid} memberName={member.member_name} overrideCreditScore={member.credit_score} />;
                            case 'total_outstanding_adverse_debt': return <AdverseDebtChart data={filteredData} showDebug={false} />;
                            case 'number_of_late_payments': return <NumberOfLatePaymentsChart data={filteredData} showDebug={false} memberLatePayments={member.number_of_late_payments} />;
                            case 'dti_ratio': return <DebtToIncomeChart data={filteredData.map(d => ({ ...d, dti_ratio: d.dti_ratio != null ? Number(d.dti_ratio) : 0 }))} showDebug={false} monthsToShow={12} runId={member.run_id} memberId={member.uuid} memberName={member.member_name} />;
                            case 'liquid_reserves': return <ActualCashOnHandChart data={filteredData} showDebug={false} />;
                            default: return renderChart(metric, filteredData, false, 12, undefined, { runId: member.run_id, memberId: member.uuid, memberName: member.member_name, memberLatePayments: member.number_of_late_payments });
                          }
                        };

                        // Map metric.key to uiFieldPath for Evidence Modal
                        const metricKeyToUiFieldPath: Record<string, string> = {
                          'gross_income': 'lender.current_finances.gross_income',
                          'minimum_debt_payments': 'lender.current_finances.minimum_debt_payments',
                          'liquid_reserves': 'lender.current_finances.cash_on_hand',
                          'money_in': 'lender.current_finances.money_in',
                          'money_leftover': 'lender.current_finances.money_leftover',
                          'dti_ratio': 'lender.current_finances.dti_ratio',
                          'monthly_expenses': 'lender.current_finances.monthly_expenses',
                          'net_savings': 'lender.current_finances.net_savings',
                          'total_outstanding_adverse_debt': 'lender.current_finances.outstanding_debt',
                          'number_of_late_payments': 'lender.current_finances.late_payments',
                          'credit_score': 'lender.current_finances.credit_score',
                        };
                        const uiFieldPath = metricKeyToUiFieldPath[metric.key as string];

                        const metricClass = selectedMetric.key === metric.key ? 'bg-blue-50 text-blue-700' : '';

                        // BUG-UI-079 FIX: Display value with month indicator if from different month
                        const displayValue = formattedValue + monthIndicator;

                        return (
                          <DebugSection key={String(metric.key)} label={`Section 4A-2-${index + 1}: ${metric.label}`}>
                            {isMobile ? (
                              <MetricTrigger
                                label={metric.label}
                                value={displayValue}
                                chartComponent={getMetricChartComponent()}
                                className={metricClass}
                              />
                            ) : (
                              <div
                                onClick={() => setSelectedMetric(metric)}
                                className={`w-full flex justify-between items-center py-1.5 px-3 rounded transition-colors cursor-pointer ${selectedMetric.key === metric.key ? 'bg-blue-50 text-blue-700' : 'hover:bg-slate-50'}`}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setSelectedMetric(metric); }}
                              >
                                <div className="font-semibold">{metric.label}</div>
                                {uiFieldPath ? (
                                  <div onClick={(e) => e.stopPropagation()}>
                                    {/* BUG-UI-120: Pass dashboardValue for reconciliation with modal */}
                                    {/* Option 3: Pass initialMonth so modal opens at selected month */}
                                    <ClickableMetric
                                      uiFieldPath={uiFieldPath}
                                      runId={member.run_id || ''}
                                      memberId={member.uuid}
                                      memberName={member.member_name}
                                      dashboardValue={{
                                        value: metricValue,
                                        formattedValue: formattedValue,
                                        month: dataMonth
                                          ? new Date(dataMonth).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
                                          : undefined,
                                        unit: metric.format === 'currency' ? 'USD' : metric.format === 'percent' ? 'PERCENT' : 'COUNT',
                                      }}
                                      initialMonth={effectiveMonth}
                                    >
                                      {/* BUG-UI-079: Show month indicator in muted color if from different month */}
                                      <div className="font-medium">
                                        {formattedValue}
                                        {isFromDifferentMonth && monthIndicator && (
                                          <span className="text-xs text-slate-400 ml-1">{monthIndicator}</span>
                                        )}
                                      </div>
                                    </ClickableMetric>
                                  </div>
                                ) : (
                                  <div className="font-medium">
                                    {formattedValue}
                                    {isFromDifferentMonth && monthIndicator && (
                                      <span className="text-xs text-slate-400 ml-1">{monthIndicator}</span>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                          </DebugSection>
                        );
                      })}
                  </DebugSection>
                </DebugSection>

                {/* Right Column: Graph - Only visible on non-mobile */}
                {!isMobile && (
                  <DebugSection label="Section 4B: Metric Visualization" className="h-full">
                    <DebugSection label="Section 4B-1: Chart Container" className="h-[400px] max-sm:h-[300px]">
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-medium">{selectedMetric.label} Trend</h3>
                        <button
                          onClick={() => {
                            if (financeChartRef.current?.openModal) financeChartRef.current.openModal();
                          }}
                          className="p-1 rounded text-slate-500 hover:text-slate-700 hover:bg-slate-100 flex items-center gap-1 text-xs"
                          title="Expand chart"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="15 3 21 3 21 9"></polyline>
                            <polyline points="9 21 3 21 3 15"></polyline>
                            <line x1="21" y1="3" x2="14" y2="10"></line>
                            <line x1="3" y1="21" x2="10" y2="14"></line>
                          </svg>
                          <span>Expand</span>
                        </button>
                      </div>
                      <ResponsiveContainer width="100%" height="100%">
                        {renderChart(selectedMetric, stats, showDebug, 12, financeChartRef, { runId: member.run_id, memberId: member.uuid, memberName: member.member_name, memberLatePayments: member.number_of_late_payments }) || <div>No chart data available</div>}
                      </ResponsiveContainer>
                    </DebugSection>
                  </DebugSection>
                )}
              </div>
            </DebugSection>
              </>
            )}
          </>
        )}
      </div>
    </DebugLayout>
  );
};

export default MemberCard;
// MemberCard.tsx - Ready for your code